import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BookOpen, Calendar, MessageCircle, Star, Package, Wallet, Check, HandHeart, Mail, Send, Trash, Ticket, CalendarCheck, X as XIcon, Film, Copy, FileSpreadsheet, Users, Download, ThumbsUp, ThumbsDown, AlertTriangle, Search, Facebook, Sparkles, RefreshCw, Stethoscope, Gift } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminVideos from "@/pages/AdminVideos";
import AdminAccounts from "@/pages/AdminAccounts";

const emptyEvent = { title: "", description: "", starts_at: "", duration_minutes: 60, price: 20, capacity: 30, image_url: "", image_alt: "", category: "meditation", series: "", external_join_url: "", recurrence_frequency: "", recurrence_count: 1, replay_available: false };
const emptyArticle = { title: "", excerpt: "", content: "", image_url: "", image_alt: "", slug: "" };
const emptyPost = { title: "", content: "", image_url: "", image_alt: "", video_url: "" };
const emptyCode = { code: "", kind: "booking_free", trial_days: 30, max_total_uses: "", max_per_user: 1, expires_at: "", notes: "" };
const DAYS_FR = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AdminPage() {
    const { user, loading } = useAuth();
    const [events, setEvents] = useState([]);
    const [eventsFilter, setEventsFilter] = useState("upcoming"); // "upcoming" | "past" | "all"
    const [articles, setArticles] = useState([]);
    const [posts, setPosts] = useState([]);
    const [packs, setPacks] = useState([]);
    const [payments, setPayments] = useState([]);
    const [accSummary, setAccSummary] = useState(null);
    const [waitlist, setWaitlist] = useState([]);
    const [limitsDraft, setLimitsDraft] = useState({ accompagnement_essentiel: "", accompagnement_profond: "" });
    const [postForm, setPostForm] = useState(emptyPost);
    const [postOpen, setPostOpen] = useState(false);
    const [comments, setComments] = useState([]);
    const [evForm, setEvForm] = useState(emptyEvent);
    const [evEditId, setEvEditId] = useState(null);
    const [evOpen, setEvOpen] = useState(false);
    const [evSaving, setEvSaving] = useState(false);
    const [arForm, setArForm] = useState(emptyArticle);    const [arEditId, setArEditId] = useState(null);
    const [arOpen, setArOpen] = useState(false);
    const [arPreview, setArPreview] = useState(false);
    const [codes, setCodes] = useState([]);
    const [adminTab, setAdminTab] = useState("events");
    const [codeForm, setCodeForm] = useState(emptyCode);
    const [codeOpen, setCodeOpen] = useState(false);
    const [gcal, setGcal] = useState({ configured: false, connected: false, email: null });
    const [availability, setAvailability] = useState({ weekly: [], slot_step_minutes: 30, buffer_minutes: 15, timezone: "Europe/Berlin" });
    const [replayEvent, setReplayEvent] = useState(null);
    const [replayUrl, setReplayUrl] = useState("");
    // Danger zone — full customer data wipe
    const [resetOpen, setResetOpen] = useState(false);
    const [resetInput, setResetInput] = useState("");
    const [resetBusy, setResetBusy] = useState(false);
    const [replayMsg, setReplayMsg] = useState("");
    const [replayBusy, setReplayBusy] = useState(false);
    const [orders, setOrders] = useState([]);
    const [ordersFilter, setOrdersFilter] = useState({ status: "all", method: "all" });
    const [subs, setSubs] = useState([]);
    const [subsFilter, setSubsFilter] = useState("all");
    const [adminBookings, setAdminBookings] = useState([]);
    const [bookingsFilter, setBookingsFilter] = useState("upcoming"); // upcoming | past | all
    const [diagOrderInput, setDiagOrderInput] = useState("");
    const [diagOrderData, setDiagOrderData] = useState(null);
    const [diagOrderBusy, setDiagOrderBusy] = useState(false);
    const [welcomeGift, setWelcomeGift] = useState(null);
    const [wgForm, setWgForm] = useState({ title: "", description: "", duration_minutes: "", is_active: false });
    const [wgUploadBusy, setWgUploadBusy] = useState(null); // "video" | "audio" | "cover" | null
    const [exportMonth, setExportMonth] = useState(() => {
        const d = new Date();
        return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
    const [moderation, setModeration] = useState([]);
    const [moderationFilter, setModerationFilter] = useState("pending");
    const [diagData, setDiagData] = useState(null);
    const [diagBusy, setDiagBusy] = useState(false);
    const [regsData, setRegsData] = useState(null);
    const [regsBusy, setRegsBusy] = useState(false);
    const [regSearchInput, setRegSearchInput] = useState("");
    const [regSearchData, setRegSearchData] = useState(null);
    const [regSearchBusy, setRegSearchBusy] = useState(false);

    const refresh = () => {
        http.get("/admin/events").then((r) => setEvents(r.data));
        http.get("/admin/articles").then((r) => setArticles(r.data));
        http.get("/admin/community/posts").then((r) => setPosts(r.data));
        http.get("/admin/packs").then((r) => setPacks(r.data)).catch(() => {});
        http.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
        http.get("/admin/accompagnements/summary").then((r) => {
            setAccSummary(r.data);
            setLimitsDraft({
                accompagnement_essentiel: r.data?.availability?.accompagnement_essentiel?.max ?? "",
                accompagnement_profond: r.data?.availability?.accompagnement_profond?.max ?? "",
            });
        }).catch(() => {});
        http.get("/admin/accompagnements/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
        http.get("/admin/community/comments").then((r) => setComments(r.data)).catch(() => {});
        http.get("/admin/discount-codes").then((r) => setCodes(r.data)).catch(() => {});
        http.get("/admin/google/status").then((r) => setGcal(r.data)).catch(() => {});
        http.get("/admin/availability").then((r) => setAvailability(r.data)).catch(() => {});
        http.get("/admin/orders", { params: ordersFilter }).then((r) => setOrders(r.data)).catch(() => {});
        http.get("/admin/subscriptions", { params: { filter_kind: subsFilter } }).then((r) => setSubs(r.data)).catch(() => {});
        http.get("/admin/testimonials", { params: { status: moderationFilter } }).then((r) => setModeration(r.data)).catch(() => {});
        http.get("/admin/bookings", { params: { scope: bookingsFilter } }).then((r) => setAdminBookings(r.data)).catch(() => {});
        http.get("/admin/welcome-gift").then((r) => {
            setWelcomeGift(r.data);
            setWgForm({
                title: r.data?.title || "",
                description: r.data?.description || "",
                duration_minutes: r.data?.duration_minutes || "",
                is_active: !!r.data?.is_active,
            });
        }).catch(() => {});
    };

    const backfillGoogleDates = async () => {
        if (!window.confirm("Réaligner les dates des témoignages d'origine sur leurs vraies dates de publication Google Business ?\n\nAlessandra · juin 2025, Andrea · avril 2025, Petra · avril 2025, Virginie · mars 2025, Macy · février 2025, Nadia · décembre 2024, Mary · décembre 2024.\n\nLes témoignages soumis via le formulaire ne sont pas modifiés.")) return;
        try {
            const { data } = await http.post("/admin/testimonials/backfill-google-dates");
            if (data.updated_count === 0) {
                toast.info("Aucune date à mettre à jour ✓");
            } else {
                toast.success(`${data.updated_count} date(s) alignée(s) sur Google Business`);
            }
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const moderateTestimonial = async (id, status) => {
        try {
            await http.patch(`/admin/testimonials/${id}/moderate`, { status });
            toast.success(status === "approved" ? "Témoignage publié" : "Témoignage refusé");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteTestimonial = async (id) => {
        if (!window.confirm("Supprimer définitivement ce témoignage ?")) return;
        try {
            await http.delete(`/admin/testimonials/${id}`);
            toast.success("Témoignage supprimé");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);
    useEffect(() => {
        if (user?.role !== "admin") return;
        http.get("/admin/orders", { params: ordersFilter }).then((r) => setOrders(r.data)).catch(() => {});
    }, [ordersFilter, user]);
    useEffect(() => {
        if (user?.role !== "admin") return;
        http.get("/admin/subscriptions", { params: { filter_kind: subsFilter } }).then((r) => setSubs(r.data)).catch(() => {});
    }, [subsFilter, user]);
    useEffect(() => {
        if (user?.role !== "admin") return;
        http.get("/admin/testimonials", { params: { status: moderationFilter } }).then((r) => setModeration(r.data)).catch(() => {});
    }, [moderationFilter, user]);
    useEffect(() => {
        if (user?.role !== "admin") return;
        http.get("/admin/bookings", { params: { scope: bookingsFilter } }).then((r) => setAdminBookings(r.data)).catch(() => {});
    }, [bookingsFilter, user]);

    const resyncBookingGcal = async (b) => {
        try {
            const { data } = await http.post(`/admin/bookings/${b.id}/resync-gcal`);
            if (data.already_synced) {
                toast.info("Déjà synchronisée dans Google Calendar");
            } else {
                toast.success("Réservation ajoutée à Google Calendar ✓");
            }
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const consumeFromPack = async (b) => {
        if (!window.confirm(`Décompter la séance du ${new Date(b.slot_datetime).toLocaleString("fr-FR")} du Programme accéléré de ${b.user_email} ?\n\nCela va : réduire le pack de 1 crédit, confirmer la réservation, envoyer l'email de confirmation à la cliente, et pousser dans Google Calendar.`)) return;
        try {
            const { data } = await http.post(`/admin/bookings/${b.id}/consume-from-pack`);
            if (data.already_consumed) {
                toast.info("Déjà décomptée du pack");
            } else {
                toast.success("Séance décomptée du pack ✓ · cliente notifiée · GCal mis à jour");
            }
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const repairOrphanBookings = async () => {
        if (!window.confirm("Analyser toutes les réservations en attente et annuler celles dont le paiement a déjà été annulé ?\n\nCela libère les créneaux restés bloqués par un paiement Stripe/PayPal annulé.")) return;
        try {
            const { data } = await http.post("/admin/bookings/repair-orphans");
            if (data.repaired_count === 0) {
                toast.info("Aucune réservation orpheline trouvée ✓");
            } else {
                toast.success(`${data.repaired_count} réservation(s) orpheline(s) annulée(s)`);
            }
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const runOrderDiagnose = async () => {
        const order = (diagOrderInput || "").trim();
        if (!order) { toast.error("Saisis un n° de commande (ex : F000011)"); return; }
        setDiagOrderBusy(true);
        setDiagOrderData(null);
        try {
            const { data } = await http.post(`/admin/orders/${order}/diagnose-repair`);
            setDiagOrderData(data);
            if (data.repaired?.length) {
                toast.success(`${data.repaired.length} action(s) de rattrapage appliquée(s)`);
                refresh();
            } else {
                toast.info("Diagnostic terminé (aucun rattrapage nécessaire)");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDiagOrderBusy(false);
        }
    };

    const reapplySideEffects = async () => {
        const order = (diagOrderData?.tx?.order_number) || (diagOrderInput || "").trim();
        if (!order) return;
        if (!window.confirm(`Ré-appliquer les side-effects (création pack, inscription atelier…) pour ${order} ?`)) return;
        try {
            await http.post(`/admin/orders/${order}/reapply-side-effects`);
            toast.success("Side-effects ré-appliqués");
            await runOrderDiagnose();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveWelcomeGiftMeta = async () => {
        try {
            const params = new URLSearchParams();
            if (wgForm.title !== "") params.append("title", wgForm.title);
            if (wgForm.description !== "") params.append("description", wgForm.description);
            if (wgForm.duration_minutes !== "") params.append("duration_minutes", String(wgForm.duration_minutes));
            params.append("is_active", String(wgForm.is_active));
            const { data } = await http.patch(`/admin/welcome-gift?${params.toString()}`);
            setWelcomeGift(data);
            toast.success("Cadeau de bienvenue mis à jour");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const uploadWelcomeGiftFile = async (kind, file) => {
        if (!file) return;
        setWgUploadBusy(kind);
        try {
            const fd = new FormData();
            fd.append("kind", kind);
            fd.append("file", file);
            await http.post("/admin/welcome-gift/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Fichier ${kind} envoyé ✓`);
            const { data } = await http.get("/admin/welcome-gift");
            setWelcomeGift(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setWgUploadBusy(null); }
    };

    const deleteWelcomeGiftFile = async (kind) => {
        if (!window.confirm(`Supprimer le fichier ${kind} du cadeau de bienvenue ?`)) return;
        try {
            await http.delete(`/admin/welcome-gift/${kind}`);
            toast.success(`Fichier ${kind} supprimé`);
            const { data } = await http.get("/admin/welcome-gift");
            setWelcomeGift(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const generateBookingZoom = async (b) => {
        if (!window.confirm(`Générer un lien Zoom pour la réservation de ${b.user_email} (${new Date(b.slot_datetime).toLocaleString("fr-FR")}) et renvoyer l'email de confirmation avec le lien ?`)) return;
        try {
            const { data } = await http.post(`/admin/bookings/${b.id}/generate-zoom`);
            toast.success(`Zoom créé ✓ · GCal ${data.gcal_updated ? "mis à jour" : "inchangé"} · Email ${data.email_sent ? "envoyé" : "échec"}`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const downloadExport = async () => {
        try {
            const res = await http.get("/admin/accounting/export", {
                params: exportMonth,
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `comptabilite-${exportMonth.year}-${String(exportMonth.month).padStart(2, "0")}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Export ${exportMonth.month}/${exportMonth.year} téléchargé`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    // Auto-refresh orders when filters change (extra useEffects moved earlier in the file)

    if (loading || user === null) return <div className="text-center py-24 text-muted-foreground">Chargement…</div>;
    if (!user || user.role !== "admin") return <Navigate to="/" replace />;

    const openNewEvent = () => { setEvForm(emptyEvent); setEvEditId(null); setEvOpen(true); };
    const openEditEvent = (e) => {
        // Convert stored UTC ISO to LOCAL datetime-local (YYYY-MM-DDTHH:mm)
        const d = new Date(e.starts_at);
        const localISO = Number.isNaN(d.getTime())
            ? ""
            : new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setEvForm({
            ...emptyEvent, ...e,
            starts_at: localISO,
            series: e.series || "",
            recurrence_frequency: "",
            recurrence_count: 1,
        });
        setEvEditId(e.id); setEvOpen(true);
    };
    const saveEvent = async () => {
        // Anti double-submit — the whole form Save button is guarded by `evSaving`.
        // A slow backend response used to allow the admin to click multiple times
        // which created duplicate events. This guard is the last line of defense.
        if (evSaving) return;
        try {
            // Guard: invalid or missing date
            const d = new Date(evForm.starts_at);
            if (!evForm.starts_at || Number.isNaN(d.getTime())) {
                toast.error("Date & heure invalides");
                return;
            }
            setEvSaving(true);
            const basePayload = {
                ...evForm,
                starts_at: d.toISOString(),
                duration_minutes: +evForm.duration_minutes,
                price: +evForm.price,
                capacity: +evForm.capacity,
                series: evForm.series || null,
                recurrence_frequency: evForm.recurrence_count > 1 ? (evForm.recurrence_frequency || "weekly") : null,
                recurrence_count: +evForm.recurrence_count || 1,
            };
            if (evEditId) {
                // Pure single-event update — never create additional occurrences on edit
                const singlePayload = { ...basePayload, recurrence_count: 1, recurrence_frequency: null };
                await http.put(`/events/${evEditId}`, singlePayload);
                toast.success("Événement mis à jour");
            } else {
                await http.post("/events", basePayload);
                toast.success(`Événement créé${basePayload.recurrence_count > 1 ? ` (${basePayload.recurrence_count} occurrences)` : ""}`);
            }
            setEvOpen(false); refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setEvSaving(false); }
    };
    const retryZoom = async (id) => {
        try {
            await http.post(`/events/${id}/zoom/retry`);
            toast.success("Réunion Zoom créée");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const resendEventEmails = async (ev) => {
        const count = ev.registered_count || 0;
        if (count === 0) {
            toast.info("Aucun inscrit à notifier pour cet événement.");
            return;
        }
        const linkTxt = ev.external_join_url || ev.zoom_join_url
            ? "avec le lien de connexion"
            : "de confirmation (sans lien — le lien sera envoyé plus tard)";
        if (!window.confirm(`Renvoyer l'email ${linkTxt} aux ${count} inscrit(s) de « ${ev.title} » ?`)) return;
        try {
            const { data } = await http.post(`/admin/events/${ev.id}/resend-emails`);
            toast.success(`Emails renvoyés : ${data.sent}/${data.total_confirmed}${data.failed ? ` — ${data.failed} échec(s)` : ""}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const delEvent = async (id) => {
        if (!window.confirm("Supprimer cet atelier ?")) return;
        try {
            await http.delete(`/events/${id}`);
            toast.success("Atelier supprimé");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const forceAutoRegister = async (ev) => {
        if (!ev?.series) return toast.error("Cet atelier n'a pas de série (Harmonisation / Éveil / Libération / Unification).");
        if (!window.confirm(`Inscrire automatiquement tous les abonnés de la série "${ev.series}" à l'atelier "${ev.title}" et leur envoyer le mail Zoom ?`)) return;
        try {
            const { data } = await http.post(`/admin/events/${ev.id}/auto-register-subscribers`);
            toast.success(`${data.registered} inscription(s) · ${data.emails_sent} mail(s) envoyé(s)${data.already_confirmed ? ` · ${data.already_confirmed} déjà inscrit(s)` : ""}${data.emails_failed ? ` · ${data.emails_failed} mail(s) en échec` : ""}`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const diagnoseSubscribers = async (ev) => {
        setDiagBusy(true);
        try {
            const { data } = await http.get(`/admin/events/${ev.id}/subscribers-check`);
            setDiagData({ ...data, event_title: ev.title });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDiagBusy(false); }
    };

    const viewRegistrants = async (ev) => {
        setRegsBusy(true);
        try {
            const { data } = await http.get(`/admin/events/${ev.id}/registrations`);
            setRegsData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRegsBusy(false); }
    };

    const searchRegistrations = async () => {
        const q = (regSearchInput || "").trim();
        if (q.length < 3) { toast.info("Tape au moins 3 caractères (email ou nom)"); return; }
        setRegSearchBusy(true);
        try {
            const { data } = await http.get(`/admin/registrations/search`, { params: { q } });
            setRegSearchData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRegSearchBusy(false); }
    };

    const deleteRegistration = async (regId, label) => {
        if (!window.confirm(`Supprimer définitivement cette inscription ?\n\n${label}\n\nAucun paiement à rembourser (pending_payment).`)) return;
        try {
            await http.delete(`/admin/registrations/${regId}`);
            toast.success("Inscription supprimée");
            // Refresh the current search results in place
            setRegSearchData((prev) => prev ? {
                ...prev,
                registrations: prev.registrations.filter((r) => r.reg_id !== regId),
                registrations_found: Math.max(0, (prev.registrations_found || 0) - 1),
            } : prev);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const shareOnFacebook = async (e) => {
        // Direct Graph API publish (bypasses Facebook's broken share_channel dialog
        // which hangs forever on "Publication en cours" for Pages since Sept 2026).
        // Same code path as the automatic post at event creation → photo post with
        // the event cover, landscape 1.91:1, mobile-friendly reach.
        if (!window.confirm(`Publier "${e.title}" sur la Page Facebook ?`)) return;
        try {
            await http.post("/admin/facebook/reshare", { source_type: "event", source_id: e.id });
            toast.success("Publication envoyée à Facebook · elle apparaîtra sur la Page dans quelques secondes");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const shareArticleOnFacebook = async (a) => {
        if (!window.confirm(`Publier "${a.title}" sur la Page Facebook ?`)) return;
        try {
            await http.post("/admin/facebook/reshare", { source_type: "article", source_id: a.id });
            toast.success("Publication envoyée à Facebook · elle apparaîtra sur la Page dans quelques secondes");
        } catch (err) { toast.error(formatApiError(err)); }
    };


    const duplicateEvent = (e) => {
        // Pre-fill create dialog with a copy of this event (no recurrence, admin adjusts date before saving)
        const d = new Date(e.starts_at);
        const localISO = Number.isNaN(d.getTime())
            ? ""
            : new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setEvForm({
            ...emptyEvent,
            title: e.title,
            description: e.description,
            starts_at: localISO,
            duration_minutes: e.duration_minutes,
            price: e.price,
            capacity: e.capacity,
            image_url: e.image_url || "",
            category: e.category,
            series: e.series || "",
            replay_available: !!e.replay_available,
            recurrence_frequency: "",
            recurrence_count: 1,
        });
        setEvEditId(null);
        setEvOpen(true);
        toast.info("Duplication : ajuste la date puis enregistre");
    };

    const uploadArticleImage = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await http.post("/articles/media/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setArForm((f) => ({ ...f, image_url: data.url }));
            const sizeKb = Math.round((data.size_bytes || 0) / 1024);
            toast.success(`Image uploadée (${sizeKb} Ko) — URL insérée dans le champ ci-dessus`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openNewArticle = () => { setArForm(emptyArticle); setArEditId(null); setArOpen(true); };
    const openEditArticle = (a) => { setArForm({ title: a.title, excerpt: a.excerpt, content: a.content, image_url: a.image_url || "", image_alt: a.image_alt || "", slug: a.slug || "" }); setArEditId(a.id); setArOpen(true); };
    const saveArticle = async () => {
        try {
            // Send `slug` only when the admin filled it — empty string means
            // "auto-generate from title" (backend behaviour when field is absent/null).
            const payload = { ...arForm, slug: (arForm.slug || "").trim() || null };
            if (arEditId) await http.put(`/articles/${arEditId}`, payload);
            else await http.post("/articles", payload);
            toast.success("Article enregistré"); setArOpen(false); refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const delArticle = async (id) => {
        if (!window.confirm("Supprimer cet article ?")) return;
        await http.delete(`/articles/${id}`); toast.success("Supprimé"); refresh();
    };

    const saveLimits = async () => {
        try {
            await http.patch("/admin/accompagnements/limits", {
                accompagnement_essentiel: +limitsDraft.accompagnement_essentiel || 0,
                accompagnement_profond: +limitsDraft.accompagnement_profond || 0,
            });
            toast.success("Limites mises à jour"); refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const notifyWaitlist = async (entry) => {
        if (!window.confirm(`Envoyer l'email « place disponible » à ${entry.email} ?`)) return;
        try {
            await http.post(`/admin/accompagnements/waitlist/${entry.id}/notify`);
            toast.success(`${entry.email} prévenu(e)`); refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteWaitlistEntry = async (entry) => {
        if (!window.confirm(`Retirer ${entry.email} de la liste ?`)) return;
        await http.delete(`/admin/accompagnements/waitlist/${entry.id}`);
        toast.success("Retiré"); refresh();
    };

    const openNewPost = () => { setPostForm(emptyPost); setPostOpen(true); };
    const savePost = async () => {
        if (!postForm.content.trim()) return toast.error("Le contenu du post est requis.");
        try {
            await http.post("/community/posts", {
                title: postForm.title.trim() || null,
                content: postForm.content,
                image_url: postForm.image_url.trim() || null,
                image_alt: postForm.image_alt.trim() || null,
                video_url: postForm.video_url.trim() || null,
            });
            toast.success("Post publié · notifications envoyées aux abonnés");
            setPostOpen(false); refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const delPost = async (id) => {
        if (!window.confirm("Supprimer ce post et tous ses commentaires ?")) return;
        await http.delete(`/community/posts/${id}`);
        toast.success("Post supprimé"); refresh();
    };
    const delComment = async (id) => {
        if (!window.confirm("Supprimer ce commentaire ?")) return;
        await http.delete(`/admin/community/comments/${id}`);
        toast.success("Commentaire supprimé"); refresh();
    };

    const toggleFeature = async (p) => {
        try {
            await http.patch(`/community/posts/${p.id}/feature`, { featured: !p.featured });
            toast.success(!p.featured ? "Ajouté à la voix du cercle" : "Retiré de la voix du cercle");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const updatePackExpiry = async (pack) => {
        const current = pack.expires_at ? new Date(pack.expires_at).toISOString().slice(0, 10) : "";
        const input = window.prompt("Nouvelle date de fin de validité (AAAA-MM-JJ)", current);
        if (!input) return;
        const iso = new Date(input + "T23:59:59Z").toISOString();
        try {
            await http.patch(`/admin/packs/${pack.id}/expiry`, { expires_at: iso });
            toast.success("Validité mise à jour");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const deletePack = async (pack) => {
        if (!window.confirm(`Supprimer définitivement ce programme ?\n\n${pack.program_name} · ${pack.user_email} · ${pack.sessions_remaining}/${pack.sessions_total} restantes\n\nSi des réservations sont liées à ce programme, elles doivent d'abord être annulées ou re-linkées.`)) return;
        try {
            await http.delete(`/admin/packs/${pack.id}`);
            toast.success("Programme supprimé");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const dedupePacks = async () => {
        if (!window.confirm("Analyser et supprimer les programmes dupliqués ?\n\nPour chaque client ayant 2 programmes identiques achetés le même jour, le programme non utilisé sera supprimé (le programme avec des séances consommées est conservé).")) return;
        try {
            const { data } = await http.post("/admin/packs/dedupe");
            if (data.removed_count === 0) toast.info("Aucun doublon trouvé ✓");
            else toast.success(`${data.removed_count} doublon(s) supprimé(s)`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const markPaid = async (tx) => {
        const today = new Date().toISOString().slice(0, 10);
        const input = window.prompt("Date de réception du paiement (AAAA-MM-JJ)", today);
        if (!input) return;
        try {
            await http.patch(`/admin/payments/${tx.id}/mark-paid`, { received_at: new Date(input + "T12:00:00Z").toISOString() });
            toast.success("Paiement marqué comme reçu");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const unmarkPaid = async (tx) => {
        if (!window.confirm("Repasser ce paiement en attente ?")) return;
        try {
            await http.patch(`/admin/payments/${tx.id}/unmark`);
            toast.success("Paiement repassé en attente");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const cancelPayment = async (tx) => {
        const label = tx.order_number || tx.reference || tx.item_name || tx.kind;
        if (!window.confirm(`Annuler définitivement le paiement ${label} ? Les références Stripe/PayPal seront supprimées.`)) return;
        try {
            await http.patch(`/admin/payments/${tx.id}/cancel`);
            toast.success(`Paiement ${label} annulé`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const deactivateSub = async (s) => {
        if (!window.confirm(`Désactiver l'abonnement de ${s.name || s.email} ? L'accès aux avantages sera coupé immédiatement.`)) return;
        try {
            const { data } = await http.post(`/admin/subscriptions/${s.user_id}/deactivate`);
            toast.success(data.stripe_cancel_at_period_end
                ? "Abonnement désactivé (Stripe annulé en fin de période)"
                : "Abonnement désactivé");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const regenerateCalendars = async () => {
        if (!window.confirm("Régénérer les PDFs du mois courant et du mois suivant à partir des événements en base ?")) return;
        try {
            const { data } = await http.post("/admin/calendars/regenerate");
            toast.success(`Calendriers régénérés : ${(data.regenerated || []).join(", ")}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const syncPaypalPlans = async () => {
        if (!window.confirm("Provisionner (ou revalider) les plans PayPal récurrents pour tous les abonnements et accompagnements ?")) return;
        try {
            const { data } = await http.post("/admin/paypal/sync-plans");
            const count = Object.keys(data.plans || {}).length;
            const created = (data.created || []).length;
            const errors = data.errors || {};
            const errCount = Object.keys(errors).length;
            if (errCount > 0) {
                const summary = Object.entries(errors)
                    .map(([k, msg]) => `${k}: ${msg}`)
                    .join(" · ");
                toast.error(`${errCount} plan(s) en erreur — ${summary}`, { duration: 12000 });
            } else if (created > 0) {
                toast.success(`${created} plan(s) créé(s) · ${count} plan(s) PayPal actifs au total`);
            } else {
                toast.success(`${count} plan(s) PayPal déjà à jour — rien à provisionner`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Plans PayPal LIVE déjà provisionnés depuis la preview (29 août 2026) — à réutiliser
    // en production pour éviter les doublons sur PayPal.
    const KNOWN_PAYPAL_PLANS = {
        product_id: "PROD-74X85944R50432148",
        plans: {
            harmonisation_monthly: "P-3K475704ST3819140NKJUCMI",
            harmonisation_yearly: "P-4VG24678PW844930GNKJUCMQ",
            eveil_monthly: "P-9TX41024AM724830GNKJUCMQ",
            eveil_yearly: "P-3SD46033D05811459NKJUCMY",
            accompagnement_essentiel_monthly: "P-3S09608228026371CNKJUCNA",
            accompagnement_profond_monthly: "P-56K48562X3560840PNKJUCNA",
        },
    };

    const importPaypalPlans = async () => {
        if (!window.confirm("Importer les 6 plans PayPal existants dans cette base ? À utiliser en production pour réutiliser les plans créés en preview (0 doublon côté PayPal).")) return;
        try {
            const { data } = await http.post("/admin/paypal/import-plans", KNOWN_PAYPAL_PLANS);
            const count = Object.keys(data.plans || {}).length;
            const skipped = Object.keys(data.skipped || {}).length;
            toast.success(`${count} plan(s) importés · Product ${data.product_id}${skipped ? ` · ${skipped} ignoré(s)` : ""}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openNewCode = () => { setCodeForm(emptyCode); setCodeOpen(true); };
    const saveCode = async () => {
        if (!codeForm.code.trim()) return toast.error("Le code est requis.");
        const payload = {
            code: codeForm.code.trim().toUpperCase(),
            kind: codeForm.kind,
            trial_days: codeForm.kind === "subscription_trial" ? (+codeForm.trial_days || 30) : null,
            max_total_uses: codeForm.max_total_uses === "" ? null : +codeForm.max_total_uses,
            max_per_user: +codeForm.max_per_user || 1,
            expires_at: codeForm.expires_at ? new Date(codeForm.expires_at + "T23:59:59Z").toISOString() : null,
            notes: codeForm.notes || "",
            active: true,
        };
        try {
            await http.post("/admin/discount-codes", payload);
            toast.success(`Code ${payload.code} créé ✨`);
            setCodeOpen(false);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleCodeActive = async (c) => {
        try {
            await http.patch(`/admin/discount-codes/${c.id}`, { active: !c.active });
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const delCode = async (id) => {
        if (!window.confirm("Supprimer définitivement ce code ?")) return;
        await http.delete(`/admin/discount-codes/${id}`);
        toast.success("Code supprimé"); refresh();
    };

    const KIND_LABEL = {
        booking_free: "Soin/séance gratuit",
        event_free: "Atelier gratuit",
        subscription_trial: "Mois offert (abonnement)",
    };

    const connectGoogle = async () => {
        try {
            const { data } = await http.get("/admin/google/login");
            window.location.href = data.authorization_url;
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const disconnectGoogle = async () => {
        if (!window.confirm("Déconnecter votre calendrier Google ? Les créneaux ne seront plus vérifiés face à votre agenda.")) return;
        await http.post("/admin/google/disconnect");
        toast.success("Google Calendar déconnecté"); refresh();
    };
    const addRange = (day) => setAvailability((a) => ({ ...a, weekly: [...a.weekly, { day, start: "09:00", end: "12:00" }] }));
    const updateRange = (idx, field, value) => setAvailability((a) => {
        const w = [...a.weekly]; w[idx] = { ...w[idx], [field]: value }; return { ...a, weekly: w };
    });
    const removeRange = (idx) => setAvailability((a) => ({ ...a, weekly: a.weekly.filter((_, i) => i !== idx) }));
    const saveAvailability = async () => {
        try {
            await http.put("/admin/availability", availability);
            toast.success("Plages de disponibilité enregistrées");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openReplay = (ev) => {
        setReplayEvent(ev);
        setReplayUrl(ev.replay_url || "");
        setReplayMsg(ev.replay_message || "");
    };
    const closeReplay = () => { setReplayEvent(null); setReplayUrl(""); setReplayMsg(""); };
    const sendReplay = async () => {
        if (!replayUrl.trim().startsWith("http")) return toast.error("URL du replay invalide.");
        setReplayBusy(true);
        try {
            const { data } = await http.post(`/admin/events/${replayEvent.id}/replay`, {
                url: replayUrl.trim(),
                message: replayMsg.trim() || null,
            });
            toast.success(`Replay envoyé à ${data.sent} participant(s)` + (data.failed?.length ? ` · ${data.failed.length} échec(s)` : ""));
            closeReplay();
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setReplayBusy(false); }
    };

    const runResetCustomerData = async () => {
        if (resetInput !== "RESET" || resetBusy) return;
        setResetBusy(true);
        try {
            const { data } = await http.post("/admin/reset-customer-data", { confirm: "RESET" });
            const s = data.summary || {};
            toast.success(
                `Réinitialisation complète — commandes: ${s.payment_transactions || 0}, réservations: ${s.bookings || 0}, inscriptions ateliers: ${s.event_registrations || 0}, programmes: ${s.packs || 0}, cadeaux: ${s.gifts || 0}, listes d'attente: ${s.accompagnement_waitlist || 0}. Compteur remis à 0 → prochaine commande F000001.`,
                { duration: 18000 }
            );
            setResetOpen(false); setResetInput(""); refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setResetBusy(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 py-16" data-testid="admin-page">
            <div className="mb-10">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Administration</div>
                <h1 className="font-serif text-5xl">Panneau admin</h1>
            </div>
            <Tabs value={adminTab} onValueChange={setAdminTab} className="w-full">
                {/* Mobile: dropdown navigation */}
                <div className="lg:hidden mb-6">
                    <label htmlFor="admin-tab-select" className="block text-xs uppercase tracking-[0.2em] text-primary mb-2 font-medium">Section</label>
                    <select
                        id="admin-tab-select"
                        value={adminTab}
                        onChange={(e) => setAdminTab(e.target.value)}
                        className="w-full h-12 rounded-2xl border border-primary/30 bg-card px-4 pr-10 text-base font-medium text-foreground appearance-none bg-no-repeat bg-[right_1rem_center] focus:outline-none focus:ring-2 focus:ring-primary/40"
                        style={{ backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")" }}
                        data-testid="admin-tab-select"
                    >
                        <option value="events">Événements</option>
                        <option value="articles">Articles</option>
                        <option value="videos">Vidéos</option>
                        <option value="community">Le cercle</option>
                        <option value="moderation">Témoignages</option>
                        <option value="packs">Programmes</option>
                        <option value="bookings">Réservations soins</option>
                        <option value="welcomegift">Cadeau infolettre</option>
                        <option value="payments">Paiements</option>
                        <option value="accounting">Comptabilité</option>
                        <option value="subscriptions">Abonnements</option>
                        <option value="accompagnements">Accompagnements</option>
                        <option value="codes">Codes cadeaux</option>
                        <option value="availability">Disponibilité</option>
                        <option value="accounts">Comptes</option>
                    </select>
                </div>
                {/* Desktop / large tablet: pill tabs */}
                <TabsList className="rounded-full mb-8 hidden lg:flex flex-wrap h-auto">
                    <TabsTrigger value="events" className="rounded-full" data-testid="tab-admin-events"><Calendar className="w-4 h-4 mr-2" /> Événements</TabsTrigger>
                    <TabsTrigger value="articles" className="rounded-full" data-testid="tab-admin-articles"><BookOpen className="w-4 h-4 mr-2" /> Articles</TabsTrigger>
                    <TabsTrigger value="videos" className="rounded-full" data-testid="tab-admin-videos"><Film className="w-4 h-4 mr-2" /> Vidéos</TabsTrigger>
                    <TabsTrigger value="community" className="rounded-full" data-testid="tab-admin-community"><MessageCircle className="w-4 h-4 mr-2" /> Le cercle</TabsTrigger>
                    <TabsTrigger value="moderation" className="rounded-full" data-testid="tab-admin-moderation"><Star className="w-4 h-4 mr-2" /> Témoignages</TabsTrigger>
                    <TabsTrigger value="packs" className="rounded-full" data-testid="tab-admin-packs"><Package className="w-4 h-4 mr-2" /> Programmes</TabsTrigger>
                    <TabsTrigger value="bookings" className="rounded-full" data-testid="tab-admin-bookings"><Sparkles className="w-4 h-4 mr-2" /> Réservations</TabsTrigger>
                    <TabsTrigger value="welcomegift" className="rounded-full" data-testid="tab-admin-welcomegift"><Mail className="w-4 h-4 mr-2" /> Cadeau infolettre</TabsTrigger>
                    <TabsTrigger value="payments" className="rounded-full" data-testid="tab-admin-payments"><Wallet className="w-4 h-4 mr-2" /> Paiements</TabsTrigger>
                    <TabsTrigger value="accounting" className="rounded-full" data-testid="tab-admin-accounting"><FileSpreadsheet className="w-4 h-4 mr-2" /> Comptabilité</TabsTrigger>
                    <TabsTrigger value="subscriptions" className="rounded-full" data-testid="tab-admin-subscriptions"><Users className="w-4 h-4 mr-2" /> Abonnements</TabsTrigger>
                    <TabsTrigger value="accompagnements" className="rounded-full" data-testid="tab-admin-accompagnements"><HandHeart className="w-4 h-4 mr-2" /> Accompagnements</TabsTrigger>
                    <TabsTrigger value="codes" className="rounded-full" data-testid="tab-admin-codes"><Ticket className="w-4 h-4 mr-2" /> Codes cadeaux</TabsTrigger>
                    <TabsTrigger value="availability" className="rounded-full" data-testid="tab-admin-availability"><CalendarCheck className="w-4 h-4 mr-2" /> Disponibilité</TabsTrigger>
                    <TabsTrigger value="accounts" className="rounded-full" data-testid="tab-admin-accounts"><Users className="w-4 h-4 mr-2" /> Comptes</TabsTrigger>
                </TabsList>
                <TabsContent value="events">
                    <div className="mb-6 rounded-3xl border border-primary/30 bg-primary/5 p-4" data-testid="registrations-search-block">
                        <div className="flex items-start gap-3">
                            <Search className="w-5 h-5 text-primary shrink-0 mt-1" />
                            <div className="flex-1">
                                <div className="text-sm font-medium mb-1">Rechercher un inscrit par email ou nom</div>
                                <p className="text-xs text-muted-foreground mb-2">
                                    Scanne TOUTES les inscriptions (confirmées, en attente, code cadeau, abonnement) — utile quand un client dit s'être inscrit et que tu ne le trouves nulle part.
                                </p>
                                <div className="flex gap-2">
                                    <Input
                                        value={regSearchInput}
                                        onChange={(e) => setRegSearchInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") searchRegistrations(); }}
                                        placeholder="ex : malgrain, catherine, @gmail.com…"
                                        className="rounded-full h-10"
                                        data-testid="registrations-search-input"
                                    />
                                    <Button onClick={searchRegistrations} disabled={regSearchBusy} className="rounded-full h-10 bg-primary text-primary-foreground shrink-0" data-testid="registrations-search-btn">
                                        {regSearchBusy ? "Recherche…" : "Chercher"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                        <div className="inline-flex rounded-full border border-border/60 bg-card p-1" data-testid="events-filter">
                            {[
                                { v: "upcoming", label: "À venir" },
                                { v: "past", label: "Archivés" },
                                { v: "all", label: "Tous" },
                            ].map(({ v, label }) => (
                                <button
                                    key={v}
                                    onClick={() => setEventsFilter(v)}
                                    data-testid={`events-filter-${v}`}
                                    className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${
                                        eventsFilter === v
                                            ? "bg-primary text-primary-foreground"
                                            : "text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <Button onClick={openNewEvent} className="rounded-full bg-primary text-primary-foreground" data-testid="new-event-btn"><Plus className="w-4 h-4 mr-2" /> Nouvel événement</Button>
                    </div>
                    <div className="grid gap-4">
                        {events
                            .filter((e) => {
                                if (eventsFilter === "all") return true;
                                const isPast = new Date(e.starts_at).getTime() < Date.now();
                                return eventsFilter === "past" ? isPast : !isPast;
                            })
                            .sort((a, b) => {
                                // Archived: most recent first (easier to find "the one I ran last week").
                                // Upcoming / All: keep the API's chronological order (nearest next date on top).
                                if (eventsFilter === "past") {
                                    return new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime();
                                }
                                return 0;
                            })
                            .map((e) => (
                            <div key={e.id} className="rounded-3xl border border-border/60 bg-card p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-event-${e.id}`}>
                                <div className="flex-1">
                                    <div className="text-xs uppercase tracking-widest text-primary mb-1">{e.category}{e.series ? ` · série ${e.series}` : ""}{e.recurrence_group_id ? " · récurrent" : ""}</div>
                                    <h3 className="font-serif text-xl">{e.title}</h3>
                                    <p className="text-sm text-muted-foreground">{new Date(e.starts_at).toLocaleString("fr-FR")} · {e.price > 0 ? `${e.price}€` : "Gratuit"} · {e.registered_count}/{e.capacity} inscrits</p>
                                    {e.replay_sent_at && (
                                        <div className="inline-flex items-center gap-1.5 mt-2 text-xs text-secondary" data-testid={`replay-sent-${e.id}`}>
                                            <Film className="w-3 h-3" /> Replay envoyé à {e.replay_sent_count || 0} participant(s) · {new Date(e.replay_sent_at).toLocaleDateString("fr-FR")}
                                        </div>
                                    )}
                                    {e.external_join_url ? (
                                        <a href={e.external_join_url} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary break-all"
                                            data-testid={`external-link-${e.id}`}>
                                            Lien externe · {e.external_join_url}
                                        </a>
                                    ) : e.zoom_join_url ? (
                                        <a href={e.zoom_join_url} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary"
                                            data-testid={`zoom-link-${e.id}`}>
                                            Lien Zoom · #{e.zoom_meeting_id}
                                        </a>
                                    ) : (
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="text-xs text-destructive">Zoom : {e.zoom_status || "non créé"}</span>
                                            <button onClick={() => retryZoom(e.id)}
                                                className="text-xs text-primary underline"
                                                data-testid={`retry-zoom-${e.id}`}>
                                                Réessayer la création
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => resendEventEmails(e)} data-testid={`resend-emails-${e.id}`} title="Renvoyer l'email aux inscrits">
                                        <Send className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => openReplay(e)} data-testid={`replay-event-${e.id}`} title="Envoyer le replay">
                                        <Film className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => duplicateEvent(e)} data-testid={`duplicate-event-${e.id}`} title="Dupliquer">
                                        <Copy className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full text-primary" onClick={() => forceAutoRegister(e)} data-testid={`auto-register-event-${e.id}`} title="Inscrire les abonnés de la série + envoyer les mails Zoom">
                                        <Users className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => viewRegistrants(e)} data-testid={`view-registrants-${e.id}`} title="Voir la liste des inscrits (payants, abonnés, codes cadeaux)">
                                        <Users className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => diagnoseSubscribers(e)} data-testid={`diagnose-event-${e.id}`} title="Diagnostic : voir qui devrait être auto-inscrit">
                                        <Search className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEditEvent(e)} data-testid={`edit-event-${e.id}`} title="Éditer"><Pencil className="w-3.5 h-3.5" /></Button>
                                    <Button variant="outline" size="sm" className="rounded-full text-[#1877F2]" onClick={() => shareOnFacebook(e)} data-testid={`share-fb-event-${e.id}`} title="Publier sur la Page Facebook (via API — photo post 1.91:1, portée mobile optimale)">
                                        <Facebook className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => delEvent(e.id)} data-testid={`delete-event-${e.id}`} title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="articles">
                    <div className="flex justify-end mb-6">
                        <Button onClick={openNewArticle} className="rounded-full bg-primary text-primary-foreground" data-testid="new-article-btn"><Plus className="w-4 h-4 mr-2" /> Nouvel article</Button>
                    </div>
                    <div className="grid gap-4">
                        {articles.map((a) => (
                            <div key={a.id} className="rounded-3xl border border-border/60 bg-card p-6 flex gap-4" data-testid={`admin-article-${a.id}`}>
                                <div className="flex-1">
                                    <h3 className="font-serif text-xl mb-1">{a.title}</h3>
                                    <p className="text-sm text-muted-foreground">{a.excerpt}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEditArticle(a)}><Pencil className="w-3.5 h-3.5" /></Button>
                                    <Button variant="outline" size="sm" className="rounded-full text-[#1877F2]" onClick={() => shareArticleOnFacebook(a)} data-testid={`share-fb-article-${a.id}`} title="Publier sur la Page Facebook (via API — photo post 1.91:1, portée mobile optimale)">
                                        <Facebook className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => delArticle(a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="videos">
                    <AdminVideos />
                </TabsContent>
                <TabsContent value="accounts">
                    <AdminAccounts />
                </TabsContent>
                <TabsContent value="community">
                    <div className="flex items-center justify-between mb-6">
                        <p className="text-sm text-muted-foreground max-w-xl">
                            Publiez des partages (texte, image, vidéo) dans <strong>Le cercle</strong>. Chaque nouveau post envoie automatiquement une notification email à tous les abonnés actifs.
                        </p>
                        <Button onClick={openNewPost} className="rounded-full bg-primary text-primary-foreground" data-testid="new-post-btn">
                            <Plus className="w-4 h-4 mr-2" /> Nouveau post
                        </Button>
                    </div>
                    <div className="grid gap-4 mb-10">
                        {posts.length === 0 && <p className="text-muted-foreground">Aucun post pour l'instant.</p>}
                        {posts.map((p) => (
                            <div key={p.id} className={`rounded-3xl border p-6 flex gap-4 ${p.featured ? "border-secondary bg-secondary/5" : "border-border/60 bg-card"}`} data-testid={`admin-post-${p.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                        <span className="font-medium">{p.user_name}</span>
                                        <span className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("fr-FR")}</span>
                                        {p.featured && <span className="text-xs bg-secondary text-secondary-foreground rounded-full px-2 py-0.5">En vedette</span>}
                                    </div>
                                    {p.title && <div className="font-serif text-lg mb-1">{p.title}</div>}
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap line-clamp-3">{p.content}</p>
                                    {(p.image_url || p.video_url) && (
                                        <div className="mt-2 text-xs text-muted-foreground flex gap-3">
                                            {p.image_url && <span>Image jointe</span>}
                                            {p.video_url && <span>Vidéo jointe</span>}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Button
                                        variant={p.featured ? "default" : "outline"}
                                        size="sm"
                                        className={`rounded-full ${p.featured ? "bg-secondary text-secondary-foreground" : ""}`}
                                        onClick={() => toggleFeature(p)}
                                        data-testid={`toggle-feature-${p.id}`}
                                    >
                                        <Star className={`w-3.5 h-3.5 mr-1 ${p.featured ? "fill-current" : ""}`} />
                                        {p.featured ? "Retirer" : "Mettre en vedette"}
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => delPost(p.id)} data-testid={`del-post-${p.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div>
                        <h3 className="font-serif text-2xl mb-4">Modération des commentaires</h3>
                        {comments.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Aucun commentaire à modérer.</p>
                        ) : (
                            <div className="grid gap-3">
                                {comments.map((c) => (
                                    <div key={c.id} className="rounded-2xl border border-border/60 bg-card p-4 flex gap-3" data-testid={`admin-comment-${c.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mb-1">
                                                <span className="font-medium text-foreground">{c.user_name}</span>
                                                <span>·</span>
                                                <span>{new Date(c.created_at).toLocaleString("fr-FR", {day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</span>
                                                <span>·</span>
                                                <span className="italic truncate max-w-xs">sur « {c.post_title} »</span>
                                            </div>
                                            <p className="text-sm whitespace-pre-wrap">{c.content}</p>
                                        </div>
                                        <Button variant="outline" size="sm" className="rounded-full text-destructive self-start" onClick={() => delComment(c.id)} data-testid={`del-comment-${c.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>
                <TabsContent value="moderation">
                    <div className="space-y-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="font-serif text-2xl">Modération des témoignages</h3>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full h-9"
                                    onClick={backfillGoogleDates}
                                    data-testid="backfill-google-dates-btn"
                                    title="Réaligne les 7 témoignages d'origine sur leurs dates Google Business réelles (one-shot)"
                                >
                                    <RefreshCw className="w-3.5 h-3.5 mr-2" /> Dates Google
                                </Button>
                                <select value={moderationFilter}
                                    onChange={(e) => setModerationFilter(e.target.value)}
                                    className="h-9 rounded-full border border-input bg-background px-3 text-xs" data-testid="moderation-filter">
                                    <option value="pending">En attente</option>
                                    <option value="approved">Publiés</option>
                                    <option value="rejected">Refusés</option>
                                </select>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            Les témoignages soumis par les membres apparaissent ici avant d'être publiés sur la page d'accueil.
                        </p>
                        {moderation.length === 0 && <p className="text-muted-foreground text-sm italic">Aucun témoignage dans cette catégorie.</p>}
                        <div className="grid gap-4">
                            {moderation.map((t) => (
                                <div key={t.id} className={`rounded-2xl border p-5 ${
                                    t.status === "approved" ? "border-primary/30 bg-primary/5" :
                                    t.status === "rejected" ? "border-destructive/30 bg-destructive/5" :
                                    "border-secondary/40 bg-card"
                                }`} data-testid={`moderation-${t.id}`}>
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                        <div className="flex gap-0.5">
                                            {[1,2,3,4,5].map((n) => (
                                                <Star key={n} className={`w-4 h-4 ${n <= t.rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />
                                            ))}
                                        </div>
                                        <span className="font-medium text-sm">{t.display_name || "?"}</span>
                                        <span className="text-xs text-muted-foreground">· {t.user_email || "compte anonyme"}</span>
                                        <span className="text-xs text-muted-foreground ml-auto">{new Date(t.created_at).toLocaleString("fr-FR")}</span>
                                    </div>
                                    <p className="text-sm leading-relaxed text-foreground/85 italic mb-4">« {t.content} »</p>
                                    <div className="flex flex-wrap gap-2">
                                        {t.status !== "approved" && (
                                            <Button size="sm" onClick={() => moderateTestimonial(t.id, "approved")} className="rounded-full bg-primary text-primary-foreground" data-testid={`approve-${t.id}`}>
                                                <ThumbsUp className="w-3.5 h-3.5 mr-1.5" /> Publier
                                            </Button>
                                        )}
                                        {t.status !== "rejected" && (
                                            <Button size="sm" variant="outline" onClick={() => moderateTestimonial(t.id, "rejected")} className="rounded-full" data-testid={`reject-${t.id}`}>
                                                <ThumbsDown className="w-3.5 h-3.5 mr-1.5" /> Refuser
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => deleteTestimonial(t.id)} className="rounded-full text-destructive border-destructive/40" data-testid={`delete-testi-${t.id}`}>
                                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="packs">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <p className="text-sm text-muted-foreground max-w-2xl">
                            Gérez les Programmes accélérés achetés par vos clients. Cliquez sur « Modifier » pour ajuster la date de fin de validité, ou « Dédoublonner » pour nettoyer les doublons dus à un webhook rejoué.
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full h-9"
                            onClick={dedupePacks}
                            data-testid="dedupe-packs-btn"
                            title="Supprime les programmes dupliqués (webhook rejoué)"
                        >
                            <RefreshCw className="w-3.5 h-3.5 mr-2" /> Dédoublonner
                        </Button>
                    </div>
                    <div className="grid gap-4">
                        {packs.length === 0 && <p className="text-muted-foreground">Aucun programme actif.</p>}
                        {packs.map((p) => (
                            <div key={p.id} className={`rounded-3xl border p-6 flex flex-col md:flex-row md:items-center gap-4 ${p.is_expired ? "border-border/60 bg-muted/40 opacity-70" : "border-secondary/40 bg-card"}`} data-testid={`admin-pack-${p.id}`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium">{p.program_name}</span>
                                        {p.is_expired && <span className="text-xs bg-destructive/15 text-destructive rounded-full px-2 py-0.5">Expiré</span>}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {p.user_email} · Acheté le {new Date(p.purchased_at).toLocaleDateString("fr-FR")}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Fin de validité : {p.expires_at ? new Date(p.expires_at).toLocaleDateString("fr-FR") : "—"}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-serif text-2xl">{p.sessions_remaining}<span className="text-base text-muted-foreground">/{p.sessions_total}</span></div>
                                    <div className="text-xs text-muted-foreground">restantes</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => updatePackExpiry(p)} data-testid={`edit-pack-expiry-${p.id}`}>
                                        <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                                    </Button>
                                    <Button variant="outline" size="sm" className="rounded-full text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => deletePack(p)} data-testid={`delete-pack-${p.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="bookings">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <p className="text-sm text-muted-foreground max-w-2xl">
                            Toutes les séances de soins de lumière et connexions à ses guides réservées par tes clients. Le crayon « ↻ Sync GCal » pousse une réservation manquante dans ton Google Calendar.
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                className="rounded-full h-10"
                                onClick={repairOrphanBookings}
                                data-testid="repair-orphan-bookings-btn"
                                title="Annule les réservations encore en attente alors que leur paiement a déjà été annulé"
                            >
                                <RefreshCw className="w-3.5 h-3.5 mr-2" /> Réparer orphelines
                            </Button>
                            <select
                                value={bookingsFilter}
                                onChange={(e) => setBookingsFilter(e.target.value)}
                                className="h-10 rounded-full border border-input bg-background px-4 text-sm"
                                data-testid="admin-bookings-filter"
                            >
                                <option value="upcoming">À venir</option>
                                <option value="past">Passées</option>
                                <option value="all">Toutes</option>
                                <option value="cancelled">Annulées</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid gap-3" data-testid="admin-bookings-list">
                        {adminBookings.length === 0 && <p className="text-muted-foreground text-sm">Aucune réservation dans cette catégorie.</p>}
                        {adminBookings.map((b) => {
                            const dt = b.slot_datetime ? new Date(b.slot_datetime) : null;
                            const sourceLabel = { paid: "💳 Payé", pack: "📦 Pack", gift: "🎁 Cadeau" }[b.source] || b.source;
                            const sourceColor = { paid: "bg-primary/10 text-primary", pack: "bg-secondary/15 text-secondary", gift: "bg-emerald-500/10 text-emerald-700" }[b.source] || "bg-muted";
                            return (
                                <div key={b.id} className="rounded-2xl border border-border/60 bg-card p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-center" data-testid={`admin-booking-${b.id}`}>
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <span className="font-medium">{b.session_name}</span>
                                            <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${sourceColor}`}>{sourceLabel}</span>
                                            {b.status !== "confirmed" && (
                                                <span className="text-[10px] uppercase tracking-widest bg-secondary/20 text-secondary-foreground rounded-full px-2 py-0.5">{b.status}</span>
                                            )}
                                            {b.has_gcal ? (
                                                <span className="text-[10px] uppercase tracking-widest bg-emerald-500/10 text-emerald-700 rounded-full px-2 py-0.5">GCal ✓</span>
                                            ) : (
                                                <span className="text-[10px] uppercase tracking-widest bg-destructive/10 text-destructive rounded-full px-2 py-0.5">GCal ✗</span>
                                            )}
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            {b.user_name} · {b.user_email}
                                        </div>
                                        {b.notes && (
                                            <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">Notes : {b.notes}</div>
                                        )}
                                        {b.zoom_join_url && (
                                            <div className="text-xs mt-2 flex items-center gap-2 flex-wrap" data-testid={`booking-zoom-${b.id}`}>
                                                <a
                                                    href={b.zoom_join_url}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="inline-flex items-center gap-1 rounded-full bg-[#2D8CFF]/10 text-[#2D8CFF] hover:bg-[#2D8CFF]/20 transition-colors px-2.5 py-1 font-medium"
                                                >
                                                    <Sparkles className="w-3 h-3" /> Rejoindre Zoom
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={() => { navigator.clipboard.writeText(b.zoom_join_url); toast.success("Lien Zoom copié"); }}
                                                    className="text-muted-foreground hover:text-foreground underline underline-offset-2 truncate max-w-[280px] text-left"
                                                    title="Cliquer pour copier"
                                                    data-testid={`booking-zoom-copy-${b.id}`}
                                                >
                                                    {b.zoom_join_url}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-sm text-right">
                                        {dt ? (
                                            <div className="font-medium">
                                                {dt.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                                                <br />
                                                <span className="text-muted-foreground">{dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                                            </div>
                                        ) : "—"}
                                    </div>
                                    {!b.has_gcal && b.status === "confirmed" && (
                                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => resyncBookingGcal(b)} data-testid={`resync-gcal-${b.id}`}>
                                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync GCal
                                        </Button>
                                    )}
                                    {b.status === "confirmed" && !b.zoom_join_url && (
                                        <Button variant="outline" size="sm" className="rounded-full border-primary/50 text-primary" onClick={() => generateBookingZoom(b)} data-testid={`gen-zoom-${b.id}`}>
                                            <Sparkles className="w-3.5 h-3.5 mr-1" /> Générer Zoom
                                        </Button>
                                    )}
                                    {b.status === "pending_payment" && (
                                        <Button variant="outline" size="sm" className="rounded-full text-secondary border-secondary/50" onClick={() => consumeFromPack(b)} data-testid={`consume-pack-${b.id}`}>
                                            <Package className="w-3.5 h-3.5 mr-1" /> Décompter du pack
                                        </Button>
                                    )}
                                    {b.has_gcal && b.zoom_join_url && b.status === "confirmed" && <div />}
                                </div>
                            );
                        })}
                    </div>
                </TabsContent>
                <TabsContent value="welcomegift">
                    <div className="max-w-3xl">
                        <div className="rounded-3xl border-2 border-[#c9a66b]/40 bg-[#c9a66b]/5 p-6 mb-8" data-testid="wg-panel">
                            <div className="flex items-start gap-3 mb-2">
                                <Gift className="w-5 h-5 text-[#c9a66b] shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-serif text-xl mb-1">🎁 Cadeau de bienvenue infolettre</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Ce cadeau est proposé sur la page d'accueil, dans le popup et sur la page inscription, puis envoyé dans l'email de bienvenue. Uploade la vidéo (mp4), l'audio (m4a) et l'image de couverture, puis active-le.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <Label>Titre affiché</Label>
                                <Input value={wgForm.title} onChange={(e) => setWgForm({ ...wgForm, title: e.target.value })} placeholder="Replay Atelier Harmonisation de l'être du 14 septembre" className="rounded-full h-11" data-testid="wg-title" />
                            </div>
                            <div>
                                <Label>Description (courte)</Label>
                                <Textarea value={wgForm.description} onChange={(e) => setWgForm({ ...wgForm, description: e.target.value })} placeholder="Un premier atelier vibratoire offert à toute nouvelle inscription à l'infolettre." rows={3} className="rounded-2xl" data-testid="wg-description" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Durée (minutes)</Label>
                                    <Input type="number" min="1" value={wgForm.duration_minutes} onChange={(e) => setWgForm({ ...wgForm, duration_minutes: e.target.value })} placeholder="18" className="rounded-full h-11" data-testid="wg-duration" />
                                </div>
                                <div className="flex items-end">
                                    <label className="flex items-center gap-2 cursor-pointer h-11 px-4 rounded-full border border-border/60 bg-card">
                                        <input type="checkbox" checked={wgForm.is_active} onChange={(e) => setWgForm({ ...wgForm, is_active: e.target.checked })} className="h-4 w-4 accent-primary" data-testid="wg-active" />
                                        <span className="text-sm">Actif (visible sur le site)</span>
                                    </label>
                                </div>
                            </div>
                            <Button onClick={saveWelcomeGiftMeta} className="rounded-full bg-primary text-primary-foreground" data-testid="wg-save-meta">
                                <Check className="w-4 h-4 mr-2" /> Enregistrer les infos
                            </Button>
                        </div>

                        <div className="mt-10 grid gap-4">
                            {[
                                { kind: "video", label: "Vidéo (.mp4)", accept: "video/mp4,video/mov,video/webm", filename: welcomeGift?.video_filename, url: welcomeGift?.video_url },
                                { kind: "audio", label: "Audio (.m4a, facultatif)", accept: "audio/m4a,audio/mp4,audio/mpeg,audio/x-m4a,audio/ogg", filename: welcomeGift?.audio_filename, url: welcomeGift?.audio_url },
                                { kind: "cover", label: "Image de couverture (jpg/png/webp)", accept: "image/jpeg,image/png,image/webp", filename: welcomeGift?.cover_filename, url: welcomeGift?.cover_url },
                            ].map((f) => (
                                <div key={f.kind} className="rounded-2xl border border-border/60 bg-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`wg-upload-${f.kind}`}>
                                    <div className="flex-1">
                                        <div className="font-medium">{f.label}</div>
                                        {f.filename ? (
                                            <div className="text-xs text-muted-foreground mt-1 font-mono truncate">{f.filename}</div>
                                        ) : (
                                            <div className="text-xs text-muted-foreground mt-1 italic">Aucun fichier</div>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        accept={f.accept}
                                        onChange={(e) => uploadWelcomeGiftFile(f.kind, e.target.files?.[0])}
                                        className="text-xs"
                                        disabled={wgUploadBusy === f.kind}
                                        data-testid={`wg-file-${f.kind}`}
                                    />
                                    {f.url && (
                                        <a href={f.url} target="_blank" rel="noopener noreferrer">
                                            <Button variant="outline" size="sm" className="rounded-full" data-testid={`wg-preview-${f.kind}`}>
                                                <Download className="w-3.5 h-3.5 mr-1" /> Voir
                                            </Button>
                                        </a>
                                    )}
                                    {f.filename && (
                                        <Button variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => deleteWelcomeGiftFile(f.kind)} data-testid={`wg-delete-${f.kind}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                    {wgUploadBusy === f.kind && <span className="text-xs text-muted-foreground">Upload en cours…</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="payments">
                    <p className="text-sm text-muted-foreground mb-6">
                        Paiements manuels (virement bancaire ou PayPal). Cochez la réception pour activer le service correspondant.
                    </p>
                    <div className="grid gap-4">
                        {payments.length === 0 && <p className="text-muted-foreground">Aucun paiement manuel en cours.</p>}
                        {payments.map((tx) => (
                            <div key={tx.id} className={`rounded-3xl border p-6 flex flex-col md:flex-row md:items-center gap-4 ${tx.payment_status === "paid" ? "border-primary/30 bg-primary/5" : "border-secondary/50 bg-card"}`} data-testid={`admin-payment-${tx.id}`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium">{tx.item_name || tx.kind}</span>
                                        <span className="text-xs bg-muted rounded-full px-2 py-0.5">{tx.payment_method === "transfer" ? "Virement" : tx.payment_method === "paypal_subscription" ? "PayPal récurrent" : "PayPal"}</span>
                                        {tx.is_renewal && (
                                            <span className="text-xs bg-secondary/20 text-secondary-foreground rounded-full px-2 py-0.5" title="Paiement de renouvellement automatique">🔁 Renouvellement</span>
                                        )}
                                        {tx.payment_status === "paid" && <span className="text-xs bg-primary/15 text-primary rounded-full px-2 py-0.5">Reçu</span>}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {tx.user_name} · {tx.user_email} · Réf. <strong>{tx.reference}</strong>{tx.order_number ? <> · N° <strong className="font-mono">{tx.order_number}</strong></> : null}
                                        {tx.is_renewal && tx.parent_order_number && (
                                            <> · issu de <a href={`#order-${tx.parent_order_number}`} className="font-mono text-primary underline decoration-primary/40 hover:decoration-primary" title="Voir la commande d'origine">{tx.parent_order_number}</a></>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Créé le {new Date(tx.created_at).toLocaleDateString("fr-FR")}
                                        {tx.received_at && ` · Reçu le ${new Date(tx.received_at).toLocaleDateString("fr-FR")}`}
                                    </div>
                                </div>
                                <div className="text-right font-serif text-xl">{tx.amount ? `${(tx.amount / 100).toFixed(2)}€` : "—"}</div>
                                {tx.payment_status === "paid" ? (
                                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => unmarkPaid(tx)} data-testid={`unmark-payment-${tx.id}`}>
                                        Repasser en attente
                                    </Button>
                                ) : (
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" className="rounded-full text-primary" onClick={() => markPaid(tx)} data-testid={`mark-payment-${tx.id}`}>
                                            <Check className="w-3.5 h-3.5 mr-1" /> Marquer reçu
                                        </Button>
                                        <Button variant="outline" size="sm" className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => cancelPayment(tx)} data-testid={`cancel-payment-${tx.id}`}>
                                            <XIcon className="w-3.5 h-3.5 mr-1" /> Annuler
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </TabsContent>
                <TabsContent value="accounting">
                    <div className="space-y-8">
                        {/* Order diagnostic tool */}
                        <div className="rounded-3xl border border-secondary/40 bg-secondary/5 p-6" data-testid="order-diagnose-block">
                            <div className="flex items-start gap-3 mb-4">
                                <Stethoscope className="w-5 h-5 text-secondary shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-serif text-xl mb-1">Diagnostic d'une commande</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Colle un n° de commande (ex : <span className="font-mono">F000011</span>) pour vérifier que le pack / l'inscription / la réservation a bien été créé(e) suite au paiement.
                                        Un bouton de rattrapage s'affiche si un side-effect est manquant.
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-end gap-3">
                                <div>
                                    <Label className="text-xs">N° de commande</Label>
                                    <Input
                                        value={diagOrderInput}
                                        onChange={(e) => setDiagOrderInput(e.target.value)}
                                        placeholder="F000011"
                                        className="rounded-full h-11 w-44 font-mono"
                                        data-testid="diag-order-input"
                                    />
                                </div>
                                <Button onClick={runOrderDiagnose} disabled={diagOrderBusy} className="rounded-full h-11 bg-secondary text-secondary-foreground" data-testid="diag-order-run">
                                    {diagOrderBusy ? "Analyse…" : <><Stethoscope className="w-4 h-4 mr-2" /> Diagnostiquer</>}
                                </Button>
                                {diagOrderData && (
                                    <Button onClick={reapplySideEffects} variant="outline" className="rounded-full h-11" data-testid="diag-order-reapply">
                                        <RefreshCw className="w-4 h-4 mr-2" /> Ré-appliquer side-effects
                                    </Button>
                                )}
                            </div>
                            {diagOrderData && (
                                <div className="mt-5 rounded-2xl bg-card border border-border/60 p-4 text-sm space-y-3" data-testid="diag-order-result">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div><span className="text-muted-foreground">Kind</span><br /><strong>{diagOrderData.tx?.kind}</strong></div>
                                        <div><span className="text-muted-foreground">Statut</span><br /><strong>{diagOrderData.tx?.payment_status}</strong></div>
                                        <div><span className="text-muted-foreground">Méthode</span><br /><strong>{diagOrderData.tx?.payment_method || "—"}</strong></div>
                                        <div><span className="text-muted-foreground">Montant</span><br /><strong>{diagOrderData.tx?.amount ? (diagOrderData.tx.amount / 100).toFixed(2) + "€" : "—"}</strong></div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Vérifications</div>
                                        <ul className="space-y-1">
                                            {(diagOrderData.checks || []).map((c, i) => (
                                                <li key={i} className="text-sm">{c}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    {(diagOrderData.repaired || []).length > 0 && (
                                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                                            <div className="text-xs uppercase tracking-widest text-emerald-700 mb-1">Rattrapages appliqués</div>
                                            <ul className="text-sm space-y-1">
                                                {diagOrderData.repaired.map((r, i) => (
                                                    <li key={i}>✅ {r}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Monthly export */}
                        <div className="rounded-3xl border border-primary/30 bg-primary/5 p-6" data-testid="accounting-export-block">
                            <h3 className="font-serif text-2xl mb-2">Export mensuel comptable</h3>
                            <p className="text-sm text-muted-foreground mb-5">
                                Télécharge un fichier CSV (compatible Excel/Numbers) reprenant toutes les commandes payées du mois choisi :
                                n° commande, date, client, désignation, code cadeau, montants brut et net, mode de paiement et référence.
                            </p>
                            <div className="flex flex-wrap items-end gap-3">
                                <div>
                                    <Label className="text-xs">Année</Label>
                                    <Input type="number" min="2024" max="2100" value={exportMonth.year}
                                        onChange={(e) => setExportMonth({ ...exportMonth, year: +e.target.value || new Date().getFullYear() })}
                                        className="rounded-full h-11 w-28" data-testid="export-year" />
                                </div>
                                <div>
                                    <Label className="text-xs">Mois</Label>
                                    <select value={exportMonth.month}
                                        onChange={(e) => setExportMonth({ ...exportMonth, month: +e.target.value })}
                                        className="h-11 rounded-full border border-input bg-background px-4 text-sm" data-testid="export-month">
                                        {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"].map((m, i) => (
                                            <option key={i} value={i+1}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                                <Button onClick={downloadExport} className="rounded-full h-11 bg-primary text-primary-foreground" data-testid="export-download">
                                    <Download className="w-4 h-4 mr-2" /> Télécharger le CSV
                                </Button>
                            </div>
                        </div>
                        {/* Chronological orders list */}
                        <div>
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                <h3 className="font-serif text-2xl">Toutes les commandes</h3>
                                <div className="flex gap-2">
                                    <select value={ordersFilter.status}
                                        onChange={(e) => setOrdersFilter({ ...ordersFilter, status: e.target.value })}
                                        className="h-9 rounded-full border border-input bg-background px-3 text-xs" data-testid="orders-filter-status">
                                        <option value="all">Tous statuts</option>
                                        <option value="paid">Payées</option>
                                        <option value="pending">En attente</option>
                                    </select>
                                    <select value={ordersFilter.method}
                                        onChange={(e) => setOrdersFilter({ ...ordersFilter, method: e.target.value })}
                                        className="h-9 rounded-full border border-input bg-background px-3 text-xs" data-testid="orders-filter-method">
                                        <option value="all">Tous modes</option>
                                        <option value="stripe">Carte (Stripe)</option>
                                        <option value="paypal">PayPal</option>
                                        <option value="transfer">Virement</option>
                                    </select>
                                </div>
                            </div>
                            {orders.length === 0 && <p className="text-muted-foreground text-sm">Aucune commande à afficher.</p>}
                            <div className="grid gap-3">
                                {orders.map((o) => (
                                    <div key={o.id} id={`order-${o.order_number}`} className={`rounded-2xl border p-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center scroll-mt-24 ${o.payment_status === "paid" ? "border-primary/30 bg-card" : "border-secondary/40 bg-secondary/5"}`} data-testid={`order-${o.id}`}>
                                        <div className="font-mono text-sm text-primary font-semibold min-w-[80px]">{o.order_number || "—"}</div>
                                        <div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium">{o.item_name || o.kind}</span>
                                                <span className="text-[10px] uppercase tracking-widest bg-muted rounded-full px-2 py-0.5">{o.payment_method_label}</span>
                                                {o.is_renewal && (
                                                    <span className="text-[10px] uppercase tracking-widest bg-secondary/20 text-secondary-foreground rounded-full px-2 py-0.5" title="Paiement de renouvellement automatique">🔁 Renouv.</span>
                                                )}
                                                {o.payment_status === "paid" ? (
                                                    <span className="text-[10px] uppercase tracking-widest bg-primary/15 text-primary rounded-full px-2 py-0.5">Payée</span>
                                                ) : (
                                                    <span className="text-[10px] uppercase tracking-widest bg-secondary/20 text-secondary-foreground rounded-full px-2 py-0.5">{o.payment_status}</span>
                                                )}
                                                {o.discount_code && (
                                                    <span className="text-[10px] uppercase tracking-widest bg-emerald-500/10 text-emerald-700 rounded-full px-2 py-0.5">🎁 {o.discount_code}</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                {o.user_name} · {o.user_email} · Réf. <strong>{o.payment_reference}</strong>
                                                {o.is_renewal && o.parent_order_number && (
                                                    <> · issu de <a href={`#order-${o.parent_order_number}`} className="font-mono text-primary underline decoration-primary/40 hover:decoration-primary" title="Voir la commande d'origine">{o.parent_order_number}</a></>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                Créée le {new Date(o.created_at).toLocaleString("fr-FR")}
                                                {o.paid_at && ` · Payée le ${new Date(o.paid_at).toLocaleString("fr-FR")}`}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            {o.amount_gross !== o.amount_net && (
                                                <div className="text-xs text-muted-foreground line-through">{(o.amount_gross / 100).toFixed(2)}€</div>
                                            )}
                                            <div className="font-serif text-lg">{(o.amount_net / 100).toFixed(2)}€</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="subscriptions">
                    <div className="space-y-6">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="font-serif text-2xl">Abonnements & Accompagnements</h3>
                            <select value={subsFilter}
                                onChange={(e) => setSubsFilter(e.target.value)}
                                className="h-9 rounded-full border border-input bg-background px-3 text-xs" data-testid="subs-filter">
                                <option value="all">Tous</option>
                                <option value="active">Actifs uniquement</option>
                                <option value="cancelled">Annulés uniquement</option>
                                <option value="subscription">Abonnements (Harmonisation/Éveil)</option>
                                <option value="accompagnement">Accompagnements uniquement</option>
                            </select>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            Liste des personnes ayant (ou ayant eu) un abonnement ou un accompagnement — pour un suivi rapide des statuts.
                        </p>
                        {subs.length === 0 && <p className="text-muted-foreground text-sm">Aucun abonnement à afficher.</p>}
                        <div className="grid gap-3">
                            {subs.map((s) => (
                                <div key={s.user_id} className={`rounded-2xl border p-4 flex flex-wrap items-center gap-3 ${s.status === "active" ? "border-primary/30 bg-card" : "border-border/60 bg-muted/20"}`} data-testid={`sub-${s.user_id}`}>
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium">{s.name || "—"}</span>
                                            <span className="text-xs text-muted-foreground">{s.email}</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {s.kind === "accompagnement" ? "🤝 Accompagnement" : "✨ Abonnement"} · {s.tier_label}
                                            {s.last_order_number && <> · dernière commande <span className="font-mono">{s.last_order_number}</span></>}
                                        </div>
                                        {s.started_at && (
                                            <div className="text-xs text-muted-foreground">Débuté le {new Date(s.started_at).toLocaleDateString("fr-FR")}</div>
                                        )}
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-widest rounded-full px-3 py-1 ${s.status === "active" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                                        {s.status === "active" ? "Actif" : s.status === "cancelled" ? "Annulé" : "—"}
                                    </span>
                                    {s.status === "active" && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10"
                                            onClick={() => deactivateSub(s)}
                                            data-testid={`deactivate-sub-${s.user_id}`}
                                        >
                                            <XIcon className="w-3.5 h-3.5 mr-1" /> Désactiver
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="accompagnements">
                    <div className="space-y-8" data-testid="admin-accompagnements-panel">
                        <div className="grid md:grid-cols-2 gap-4">
                            {["accompagnement_essentiel", "accompagnement_profond"].map((tier) => {
                                const av = accSummary?.availability?.[tier];
                                const pending = accSummary?.waitlist_pending?.[tier] ?? 0;
                                const label = tier === "accompagnement_essentiel" ? "Essentiel" : "Profond";
                                return (
                                    <div key={tier} className="rounded-3xl border border-border/60 bg-card p-6" data-testid={`acc-summary-${tier}`}>
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary mb-2">
                                            <HandHeart className="w-3.5 h-3.5" /> {label}
                                        </div>
                                        <div className="flex items-baseline gap-2 mb-4">
                                            <span className="font-serif text-4xl">{av?.active ?? "—"}</span>
                                            <span className="text-muted-foreground text-sm">/ {av?.max ?? "—"} places actives</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mb-4">
                                            {av?.available ?? "—"} place{(av?.available || 0) > 1 ? "s" : ""} libre{(av?.available || 0) > 1 ? "s" : ""} · {pending} personne{pending > 1 ? "s" : ""} en liste d'attente
                                        </div>
                                        <Label className="text-xs">Nombre maximum de places</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={limitsDraft[tier]}
                                            onChange={(e) => setLimitsDraft((s) => ({ ...s, [tier]: e.target.value }))}
                                            className="rounded-full h-11 mt-1"
                                            data-testid={`limit-input-${tier}`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={saveLimits} className="rounded-full bg-primary text-primary-foreground" data-testid="save-limits">
                                Enregistrer les limites
                            </Button>
                        </div>
                        <div>
                            <h3 className="font-serif text-2xl mb-4">Liste d'attente</h3>
                            {waitlist.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Aucune personne en liste d'attente.</p>
                            ) : (
                                <div className="grid gap-3">
                                    {waitlist.map((w) => (
                                        <div key={w.id} className="rounded-2xl border border-border/60 bg-card p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`waitlist-entry-${w.id}`}>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-xs uppercase tracking-widest text-primary bg-primary/10 rounded-full px-2 py-0.5">
                                                        {w.tier === "accompagnement_essentiel" ? "Essentiel" : "Profond"}
                                                    </span>
                                                    {w.notified && <span className="text-xs bg-primary/15 text-primary rounded-full px-2 py-0.5">Prévenu(e)</span>}
                                                </div>
                                                <div className="mt-1 text-sm">
                                                    <strong>{w.name || "—"}</strong> · {w.email}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    Inscrit le {new Date(w.created_at).toLocaleDateString("fr-FR")}
                                                    {w.notified_at && ` · Prévenu le ${new Date(w.notified_at).toLocaleDateString("fr-FR")}`}
                                                </div>
                                            </div>
                                            {!w.notified && (
                                                <Button variant="outline" size="sm" className="rounded-full text-primary" onClick={() => notifyWaitlist(w)} data-testid={`notify-${w.id}`}>
                                                    <Mail className="w-3.5 h-3.5 mr-1" /> Prévenir par email
                                                </Button>
                                            )}
                                            <Button variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => deleteWaitlistEntry(w)} data-testid={`delete-waitlist-${w.id}`}>
                                                <Trash className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="codes">
                    <div className="flex justify-between items-center mb-6">
                        <p className="text-sm text-muted-foreground max-w-2xl">
                            Créez des codes cadeaux à offrir : soin/séance gratuit, atelier gratuit,
                            ou mois offert sur un abonnement (essai Stripe automatique).
                        </p>
                        <Button onClick={openNewCode} className="rounded-full bg-primary text-primary-foreground" data-testid="new-code-btn">
                            <Plus className="w-4 h-4 mr-2" /> Nouveau code
                        </Button>
                    </div>
                    {codes.length === 0 ? (
                        <div className="rounded-3xl border border-dashed border-border/60 p-10 text-center text-muted-foreground text-sm">
                            Aucun code cadeau pour l'instant. Créez-en un pour commencer à offrir.
                        </div>
                    ) : (
                        <div className="grid gap-3">
                            {codes.map((c) => {
                                const isExpired = c.expires_at && new Date(c.expires_at) < new Date();
                                const isCap = c.max_total_uses && c.uses_count >= c.max_total_uses;
                                return (
                                    <div key={c.id} className="rounded-3xl border border-border/60 bg-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-code-${c.id}`}>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="font-mono text-lg font-semibold tracking-wider">{c.code}</span>
                                                <span className={`text-xs px-2.5 py-0.5 rounded-full ${
                                                    c.kind === "subscription_trial" ? "bg-secondary/20 text-secondary-foreground" :
                                                    c.kind === "event_free" ? "bg-primary/15 text-primary" : "bg-accent text-foreground/80"
                                                }`}>
                                                    {KIND_LABEL[c.kind]}
                                                </span>
                                                {c.kind === "subscription_trial" && (
                                                    <span className="text-xs text-muted-foreground">{c.trial_days} jours d'essai</span>
                                                )}
                                                {!c.active && <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">Désactivé</span>}
                                                {isExpired && <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">Expiré</span>}
                                                {isCap && <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">Épuisé</span>}
                                            </div>
                                            <div className="text-sm text-muted-foreground mt-2 flex flex-wrap gap-4">
                                                <span>Utilisations : <strong>{c.uses_count}</strong>{c.max_total_uses ? ` / ${c.max_total_uses}` : " · illimité"}</span>
                                                <span>Max par pers. : {c.max_per_user}</span>
                                                {c.expires_at && <span>Expire : {new Date(c.expires_at).toLocaleDateString("fr-FR")}</span>}
                                            </div>
                                            {c.notes && <p className="text-xs text-muted-foreground italic mt-2">{c.notes}</p>}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" className="rounded-full" onClick={() => toggleCodeActive(c)} data-testid={`toggle-code-${c.id}`}>
                                                {c.active ? "Désactiver" : "Activer"}
                                            </Button>
                                            <Button variant="outline" size="sm" className="rounded-full text-destructive" onClick={() => delCode(c.id)} data-testid={`delete-code-${c.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>
                <TabsContent value="availability">
                    <div className="grid gap-8">
                        {/* Google Calendar connection */}
                        <div className="rounded-3xl border border-border/60 bg-card p-6">
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <div>
                                    <h3 className="font-serif text-2xl mb-1">Google Calendar</h3>
                                    <p className="text-sm text-muted-foreground">
                                        Connectez votre calendrier Google (Joy2live) : les créneaux occupés vous seront bloqués automatiquement,
                                        et chaque réservation confirmée sera ajoutée à votre agenda (en privé, sans invités).
                                    </p>
                                </div>
                                {gcal.connected ? (
                                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-secondary/15 text-secondary-foreground" data-testid="gcal-connected-badge">
                                        <Check className="w-3.5 h-3.5" /> Connecté
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground">
                                        Non connecté
                                    </span>
                                )}
                            </div>
                            {gcal.connected ? (
                                <div className="flex items-center justify-between rounded-2xl bg-muted/30 px-4 py-3">
                                    <div className="text-sm">Calendrier lié : <strong>{gcal.email || "primary"}</strong></div>
                                    <Button variant="outline" className="rounded-full text-destructive" onClick={disconnectGoogle} data-testid="gcal-disconnect">Déconnecter</Button>
                                </div>
                            ) : (
                                <Button onClick={connectGoogle} className="rounded-full bg-primary text-primary-foreground" data-testid="gcal-connect">
                                    <Calendar className="w-4 h-4 mr-2" /> Connecter Google Calendar
                                </Button>
                            )}
                        </div>
                        {/* Weekly availability editor */}
                        <div className="rounded-3xl border border-border/60 bg-card p-6">
                            <div className="mb-4">
                                <h3 className="font-serif text-2xl mb-1">Plages hebdomadaires de disponibilité</h3>
                                <p className="text-sm text-muted-foreground">
                                    Définissez vos heures ouvrables. Les créneaux proposés aux clients sont calculés
                                    en croisant ces plages avec votre Google Calendar et vos réservations existantes.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div>
                                    <Label className="text-xs">Fuseau horaire</Label>
                                    <Input value={availability.timezone} onChange={(e) => setAvailability({ ...availability, timezone: e.target.value })} className="rounded-full h-11" data-testid="avail-timezone" />
                                </div>
                                <div>
                                    <Label className="text-xs">Pas des créneaux (min.)</Label>
                                    <Input type="number" min="15" max="120" value={availability.slot_step_minutes} onChange={(e) => setAvailability({ ...availability, slot_step_minutes: +e.target.value || 30 })} className="rounded-full h-11" data-testid="avail-step" />
                                </div>
                                <div>
                                    <Label className="text-xs">Battement entre séances (min.)</Label>
                                    <Input type="number" min="0" max="120" value={availability.buffer_minutes} onChange={(e) => setAvailability({ ...availability, buffer_minutes: +e.target.value || 0 })} className="rounded-full h-11" data-testid="avail-buffer" />
                                </div>
                            </div>
                            <div className="space-y-3">
                                {DAYS_FR.map((label, dayIdx) => {
                                    const rangesForDay = availability.weekly
                                        .map((r, i) => ({ ...r, i }))
                                        .filter((r) => r.day === dayIdx);
                                    return (
                                        <div key={dayIdx} className="flex items-start gap-3 rounded-2xl border border-border/40 p-3" data-testid={`avail-day-${dayIdx}`}>
                                            <div className="w-24 pt-2 text-sm font-medium">{label}</div>
                                            <div className="flex-1 flex flex-wrap gap-2">
                                                {rangesForDay.length === 0 && (
                                                    <span className="text-xs text-muted-foreground pt-2">Aucune plage — jour fermé</span>
                                                )}
                                                {rangesForDay.map((r) => (
                                                    <div key={r.i} className="flex items-center gap-1.5 bg-muted/40 rounded-full px-2 py-1">
                                                        <Input type="time" value={r.start} onChange={(e) => updateRange(r.i, "start", e.target.value)} className="h-8 w-24 rounded-full text-sm" data-testid={`avail-start-${r.i}`} />
                                                        <span className="text-xs">→</span>
                                                        <Input type="time" value={r.end} onChange={(e) => updateRange(r.i, "end", e.target.value)} className="h-8 w-24 rounded-full text-sm" data-testid={`avail-end-${r.i}`} />
                                                        <button onClick={() => removeRange(r.i)} className="p-1 rounded-full hover:bg-destructive/10 text-destructive" data-testid={`avail-remove-${r.i}`}>
                                                            <XIcon className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                ))}
                                                <Button variant="outline" size="sm" className="rounded-full h-8" onClick={() => addRange(dayIdx)} data-testid={`avail-add-${dayIdx}`}>
                                                    <Plus className="w-3.5 h-3.5 mr-1" /> Plage
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <Button onClick={saveAvailability} className="w-full mt-6 rounded-full h-12 bg-primary text-primary-foreground" data-testid="avail-save">
                                Enregistrer mes disponibilités
                            </Button>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
            <Dialog open={evOpen} onOpenChange={setEvOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">{evEditId ? "Modifier" : "Nouvel"} événement</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Titre</Label><Input value={evForm.title} onChange={(e) => setEvForm({...evForm, title: e.target.value})} className="rounded-full h-11" data-testid="event-form-title" /></div>
                        <div><Label>Description</Label><Textarea value={evForm.description} onChange={(e) => setEvForm({...evForm, description: e.target.value})} className="rounded-2xl" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Date & heure</Label><Input type="datetime-local" value={evForm.starts_at} onChange={(e) => setEvForm({...evForm, starts_at: e.target.value})} className="rounded-full h-11" /></div>
                            <div><Label>Durée (min)</Label><Input type="number" value={evForm.duration_minutes} onChange={(e) => setEvForm({...evForm, duration_minutes: e.target.value})} className="rounded-full h-11" /></div>
                            <div><Label>Prix (€)</Label><Input type="number" value={evForm.price} onChange={(e) => setEvForm({...evForm, price: e.target.value})} className="rounded-full h-11" /></div>
                            <div><Label>Capacité</Label><Input type="number" value={evForm.capacity} onChange={(e) => setEvForm({...evForm, capacity: e.target.value})} className="rounded-full h-11" /></div>
                        </div>
                        <div><Label>Catégorie</Label><Input value={evForm.category} onChange={(e) => setEvForm({...evForm, category: e.target.value})} className="rounded-full h-11" /></div>
                        <div><Label>URL de l'image (facultatif)</Label><Input value={evForm.image_url} onChange={(e) => setEvForm({...evForm, image_url: e.target.value})} className="rounded-full h-11" placeholder="https://…" /></div>
                        <div><Label>Description de l'image <span className="text-xs text-muted-foreground">(SEO & accessibilité — max 125 caractères)</span></Label><Input value={evForm.image_alt} onChange={(e) => setEvForm({...evForm, image_alt: e.target.value.slice(0,125)})} className="rounded-full h-11" placeholder="Ex. Atelier de langage de lumière — série Harmonisation de l'être" data-testid="event-form-image-alt" /></div>
                        <div>
                            <Label>Série d'atelier (facultatif)</Label>
                            <select value={evForm.series || ""} onChange={(e) => setEvForm({...evForm, series: e.target.value})}
                                className="w-full h-11 rounded-full border border-input bg-background px-4 text-sm" data-testid="event-form-series">
                                <option value="">— Aucune série —</option>
                                <option value="harmonisation">Harmonisation</option>
                                <option value="eveil">Éveil</option>
                                <option value="liberation">Libération</option>
                                <option value="unification">Unification</option>
                                <option value="cercles_lumiere">Cercles de lumière</option>
                            </select>
                        </div>
                        <label className="flex items-start gap-3 rounded-2xl border border-border/60 bg-muted/20 p-4 cursor-pointer hover:bg-muted/30 transition-colors" data-testid="event-form-replay-label">
                            <input
                                type="checkbox"
                                checked={!!evForm.replay_available}
                                onChange={(e) => setEvForm({...evForm, replay_available: e.target.checked})}
                                className="mt-0.5 h-5 w-5 rounded border-input accent-primary shrink-0 cursor-pointer"
                                data-testid="event-form-replay-checkbox"
                            />
                            <span className="text-sm text-foreground/85 leading-relaxed">
                                <strong>Un enregistrement sera disponible après l'atelier.</strong>
                                <span className="block text-xs text-muted-foreground mt-1">Affiche un badge "🎥 Replay disponible" sur la fiche publique de l'atelier.</span>
                            </span>
                        </label>
                        {!evEditId && (
                            <div>
                                <Label>Lien externe (facultatif · YouTube, Google Meet, etc.)</Label>
                                <Input
                                    value={evForm.external_join_url}
                                    onChange={(e) => setEvForm({...evForm, external_join_url: e.target.value})}
                                    className="rounded-full h-11"
                                    placeholder="https://youtube.com/live/…"
                                    data-testid="event-form-external-url"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Si renseigné, aucune réunion Zoom ne sera créée et ce lien sera envoyé aux inscrits.
                                    Ce choix est définitif après la création.
                                </p>
                            </div>
                        )}
                        {!evEditId && (
                            <div className="rounded-2xl border border-border/60 bg-muted/30 p-4 space-y-3">
                                <div className="text-sm font-medium">Répétition (facultatif)</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Nombre de séances</Label>
                                        <Input type="number" min="1" max="50" value={evForm.recurrence_count}
                                            onChange={(e) => setEvForm({...evForm, recurrence_count: e.target.value})}
                                            className="rounded-full h-11" data-testid="event-form-count" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Fréquence</Label>
                                        <select value={evForm.recurrence_frequency || ""}
                                            onChange={(e) => setEvForm({...evForm, recurrence_frequency: e.target.value})}
                                            disabled={+evForm.recurrence_count <= 1}
                                            className="w-full h-11 rounded-full border border-input bg-background px-4 text-sm disabled:opacity-50"
                                            data-testid="event-form-freq">
                                            <option value="">—</option>
                                            <option value="weekly">Chaque semaine</option>
                                            <option value="biweekly">Toutes les 2 semaines</option>
                                            <option value="monthly">Chaque mois</option>
                                        </select>
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {+evForm.recurrence_count > 1
                                        ? `${evForm.recurrence_count} événements distincts seront créés, chacun avec sa propre réunion Zoom (hôte modifiable indépendamment).`
                                        : "Laissez à 1 pour un événement ponctuel."}
                                </p>
                            </div>
                        )}
                        {evEditId && (
                            <p className="text-xs text-muted-foreground italic">
                                La récurrence est définie uniquement à la création. Pour ajouter d'autres séances, dupliquez l'événement.
                            </p>
                        )}
                        <Button onClick={saveEvent} disabled={evSaving} className="w-full rounded-full h-12 bg-primary text-primary-foreground mt-2 disabled:opacity-70" data-testid="event-save">
                            {evSaving ? "Enregistrement en cours…" : "Enregistrer"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={arOpen} onOpenChange={(v) => { setArOpen(v); if (!v) setArPreview(false); }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader className="flex-row items-center justify-between gap-4">
                        <DialogTitle className="font-serif text-2xl">{arEditId ? "Modifier" : "Nouvel"} article</DialogTitle>
                        <div className="inline-flex rounded-full border border-border/60 p-0.5 mr-6" data-testid="article-preview-toggle">
                            <button
                                type="button"
                                onClick={() => setArPreview(false)}
                                className={`px-3 py-1 text-xs rounded-full transition-colors ${!arPreview ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                                data-testid="article-preview-off"
                            >
                                Éditer
                            </button>
                            <button
                                type="button"
                                onClick={() => setArPreview(true)}
                                className={`px-3 py-1 text-xs rounded-full transition-colors ${arPreview ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                                data-testid="article-preview-on"
                            >
                                Aperçu
                            </button>
                        </div>
                    </DialogHeader>
                    {!arPreview ? (
                        <div className="space-y-3">
                            <div><Label>Titre</Label><Input value={arForm.title} onChange={(e) => setArForm({...arForm, title: e.target.value})} className="rounded-full h-11" data-testid="article-form-title" /></div>
                            <div>
                                <Label>Slug URL <span className="text-xs text-muted-foreground">(facultatif — laissez vide pour générer automatiquement depuis le titre)</span></Label>
                                <Input
                                    value={arForm.slug}
                                    onChange={(e) => setArForm({...arForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "")})}
                                    className="rounded-full h-11 font-mono text-sm"
                                    placeholder="quel-atelier-choisir-foundation-of-light"
                                    data-testid="article-form-slug"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    URL finale : <span className="font-mono">/journal/{arForm.slug || "…"}</span>
                                    {arEditId && arForm.slug && " · l'ancien slug sera conservé comme alias (redirection 301 automatique)"}
                                </p>
                            </div>
                            <div><Label>Extrait</Label><Textarea value={arForm.excerpt} onChange={(e) => setArForm({...arForm, excerpt: e.target.value})} className="rounded-2xl" /></div>
                            <div><Label>Contenu <span className="text-xs text-muted-foreground">(Markdown — clique "Aperçu" en haut pour visualiser le rendu)</span></Label><Textarea value={arForm.content} onChange={(e) => setArForm({...arForm, content: e.target.value})} className="rounded-2xl min-h-[240px] font-mono text-sm" data-testid="article-form-content" /></div>
                            <div>
                                <Label>URL de l'image</Label>
                                <div className="flex gap-2 items-center">
                                    <Input value={arForm.image_url} onChange={(e) => setArForm({...arForm, image_url: e.target.value})} className="rounded-full h-11 flex-1" placeholder="https://… ou /api/media/…" />
                                    <label className="inline-flex items-center gap-1 h-11 px-4 rounded-full bg-secondary text-secondary-foreground text-sm font-medium cursor-pointer hover:bg-secondary/80 transition-colors shrink-0" data-testid="article-image-upload-label">
                                        <span>Uploader…</span>
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/gif"
                                            className="hidden"
                                            onChange={(e) => { uploadArticleImage(e.target.files?.[0]); e.target.value = ""; }}
                                            data-testid="article-image-upload-input"
                                        />
                                    </label>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">JPG · PNG · WebP · GIF · max 10 Mo. Auto-redimensionné à 1920px max et compressé pour un chargement rapide.</p>
                            </div>
                            <div><Label>Description de l'image <span className="text-xs text-muted-foreground">(SEO & accessibilité — max 125 caractères)</span></Label><Input value={arForm.image_alt} onChange={(e) => setArForm({...arForm, image_alt: e.target.value.slice(0,125)})} className="rounded-full h-11" placeholder="Ex. Aurores boréales évoquant les codes lumineux du langage de lumière" data-testid="article-form-image-alt" /></div>
                            <Button onClick={saveArticle} className="w-full rounded-full h-12 bg-primary text-primary-foreground mt-2" data-testid="article-save">Enregistrer</Button>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="article-preview-panel">
                            {arForm.image_url && (
                                <img src={arForm.image_url} alt={arForm.image_alt || arForm.title} className="w-full rounded-2xl aspect-[16/9] object-cover" />
                            )}
                            <h1 className="font-serif text-3xl leading-tight">{arForm.title || "(titre vide)"}</h1>
                            {arForm.excerpt && <p className="text-muted-foreground italic">{arForm.excerpt}</p>}
                            <article className="article-preview leading-relaxed text-[15px]" data-testid="article-preview-content">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        h1: ({node, ...p}) => <h1 className="font-serif text-3xl mt-6 mb-3" {...p} />,
                                        h2: ({node, ...p}) => <h2 className="font-serif text-2xl mt-6 mb-3 border-b border-border/50 pb-2" {...p} />,
                                        h3: ({node, ...p}) => <h3 className="font-serif text-xl mt-5 mb-2" {...p} />,
                                        p: ({node, ...p}) => <p className="my-3" {...p} />,
                                        ul: ({node, ...p}) => <ul className="list-disc pl-6 my-3 space-y-1" {...p} />,
                                        ol: ({node, ...p}) => <ol className="list-decimal pl-6 my-3 space-y-1" {...p} />,
                                        blockquote: ({node, ...p}) => <blockquote className="border-l-4 border-primary/40 pl-4 italic my-4 text-muted-foreground" {...p} />,
                                        table: ({node, ...p}) => <table className="w-full my-4 border-collapse text-sm" {...p} />,
                                        th: ({node, ...p}) => <th className="border border-border/60 bg-muted/40 px-3 py-2 text-left font-medium" {...p} />,
                                        td: ({node, ...p}) => <td className="border border-border/60 px-3 py-2" {...p} />,
                                        a: ({node, ...p}) => <a className="text-primary underline underline-offset-2 hover:opacity-80" target="_blank" rel="noreferrer noopener" {...p} />,
                                        hr: () => <hr className="my-6 border-border/50" />,
                                        code: ({node, inline, ...p}) => inline
                                            ? <code className="bg-muted/60 rounded px-1 py-0.5 text-[13px] font-mono" {...p} />
                                            : <code className="block bg-muted/60 rounded-lg p-3 text-[13px] font-mono overflow-x-auto" {...p} />,
                                        img: ({node, ...p}) => <img className="rounded-2xl my-4 max-w-full" {...p} />,
                                    }}
                                >
                                    {arForm.content || "_(contenu vide)_"}
                                </ReactMarkdown>
                            </article>
                            <div className="pt-2 border-t border-border/60">
                                <Button onClick={saveArticle} className="w-full rounded-full h-12 bg-primary text-primary-foreground" data-testid="article-save-from-preview">Enregistrer</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={postOpen} onOpenChange={setPostOpen}>                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Nouveau partage · Le cercle</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Titre (facultatif)</Label><Input value={postForm.title} onChange={(e) => setPostForm({...postForm, title: e.target.value})} className="rounded-full h-11" data-testid="post-form-title" placeholder="Ex : Méditation de la nouvelle lune" /></div>
                        <div><Label>Message</Label><Textarea value={postForm.content} onChange={(e) => setPostForm({...postForm, content: e.target.value})} className="rounded-2xl min-h-[180px]" data-testid="post-form-content" placeholder="Le contenu de votre partage…" /></div>
                        <div><Label>URL image (facultatif)</Label><Input value={postForm.image_url} onChange={(e) => setPostForm({...postForm, image_url: e.target.value})} className="rounded-full h-11" placeholder="https://…" /></div>
                        <div><Label>Description de l'image <span className="text-xs text-muted-foreground">(SEO & accessibilité — max 125 caractères)</span></Label><Input value={postForm.image_alt} onChange={(e) => setPostForm({...postForm, image_alt: e.target.value.slice(0,125)})} className="rounded-full h-11" placeholder="Décrivez brièvement l'image" data-testid="post-form-image-alt" /></div>
                        <div><Label>URL vidéo (facultatif · YouTube / Vimeo / .mp4)</Label><Input value={postForm.video_url} onChange={(e) => setPostForm({...postForm, video_url: e.target.value})} className="rounded-full h-11" placeholder="https://youtube.com/..." data-testid="post-form-video" /></div>
                        <div className="text-xs text-muted-foreground bg-muted/40 rounded-2xl px-4 py-3">
                            💌 Tous les abonnés actifs recevront un email d'annonce dès la publication.
                        </div>
                        <Button onClick={savePost} className="w-full rounded-full h-12 bg-primary text-primary-foreground mt-2" data-testid="post-save">Publier & notifier</Button>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Nouveau code cadeau</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Code (sera converti en majuscules)</Label>
                            <Input value={codeForm.code} onChange={(e) => setCodeForm({...codeForm, code: e.target.value})} placeholder="EX : ANNIVERSAIRE2026" className="rounded-full h-11 font-mono uppercase" data-testid="code-form-code" />
                        </div>
                        <div>
                            <Label>Type de cadeau</Label>
                            <select value={codeForm.kind} onChange={(e) => setCodeForm({...codeForm, kind: e.target.value})}
                                className="w-full h-11 rounded-full border border-input bg-background px-4 text-sm" data-testid="code-form-kind">
                                <option value="booking_free">Soin/séance gratuit (100%)</option>
                                <option value="event_free">Atelier gratuit (100%)</option>
                                <option value="subscription_trial">Mois offert sur abonnement (essai)</option>
                            </select>
                        </div>
                        {codeForm.kind === "subscription_trial" && (
                            <div>
                                <Label>Durée d'essai (jours)</Label>
                                <Input type="number" min="1" max="365" value={codeForm.trial_days} onChange={(e) => setCodeForm({...codeForm, trial_days: e.target.value})} className="rounded-full h-11" data-testid="code-form-trial-days" />
                                <p className="text-xs text-muted-foreground mt-1">L'abonné ne sera pas facturé pendant cette période. Stripe débite ensuite automatiquement.</p>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Utilisations max (total)</Label>
                                <Input type="number" min="1" value={codeForm.max_total_uses} onChange={(e) => setCodeForm({...codeForm, max_total_uses: e.target.value})} placeholder="illimité" className="rounded-full h-11" data-testid="code-form-max-total" />
                            </div>
                            <div>
                                <Label>Max par personne</Label>
                                <Input type="number" min="1" value={codeForm.max_per_user} onChange={(e) => setCodeForm({...codeForm, max_per_user: e.target.value})} className="rounded-full h-11" data-testid="code-form-max-user" />
                            </div>
                        </div>
                        <div>
                            <Label>Date d'expiration (facultatif)</Label>
                            <Input type="date" value={codeForm.expires_at} onChange={(e) => setCodeForm({...codeForm, expires_at: e.target.value})} className="rounded-full h-11" data-testid="code-form-expires" />
                        </div>
                        <div>
                            <Label>Notes internes (facultatif)</Label>
                            <Textarea value={codeForm.notes} onChange={(e) => setCodeForm({...codeForm, notes: e.target.value})} placeholder="Ex : Cadeau anniversaire pour Marie" className="rounded-2xl" data-testid="code-form-notes" />
                        </div>
                        <Button onClick={saveCode} className="w-full rounded-full h-12 bg-primary text-primary-foreground mt-2" data-testid="code-save">Créer le code</Button>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={!!diagData} onOpenChange={(o) => { if (!o) setDiagData(null); }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="diagnose-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Diagnostic auto-inscription abonnés</DialogTitle>
                    </DialogHeader>
                    {diagData && (
                        <div className="space-y-4 text-sm">
                            <div className="rounded-2xl border border-border/60 p-4 bg-muted/30">
                                <div className="font-semibold mb-2">{diagData.event_title}</div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="text-muted-foreground">Série (brute) :</span> <code className="bg-background px-1.5 py-0.5 rounded">{diagData.event?.series_repr || "—"}</code></div>
                                    <div><span className="text-muted-foreground">Auto-inscription flag :</span> <strong>{diagData.event?.auto_registration_done ? "✅ True" : "❌ False"}</strong></div>
                                    <div><span className="text-muted-foreground">Débute :</span> {new Date(diagData.event?.starts_at).toLocaleString("fr-FR")}</div>
                                    <div><span className="text-muted-foreground">Forcée le :</span> {diagData.event?.auto_registration_forced_at ? new Date(diagData.event.auto_registration_forced_at).toLocaleString("fr-FR") : "—"}</div>
                                </div>
                            </div>
                            <div>
                                <div className="text-sm font-semibold mb-2">
                                    Abonnés éligibles trouvés : <span className="text-primary">{diagData.subscribers_found}</span>
                                </div>
                                {diagData.subscribers_found === 0 ? (
                                    <div className="text-xs text-destructive p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                                        Aucun abonné ne matche <code>{diagData.event?.series_repr}</code> via <code>subscription_free_series</code> ni <code>active_subscription</code>. Cela explique pourquoi le cron n'a rien fait. Vérifie l'onglet Abonnements.
                                    </div>
                                ) : (
                                    <div className="rounded-2xl border border-border/60 overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead className="bg-muted/50">
                                                <tr>
                                                    <th className="text-left p-2">Email</th>
                                                    <th className="text-left p-2">active_sub</th>
                                                    <th className="text-left p-2">free_series</th>
                                                    <th className="text-left p-2">lookup_key</th>
                                                    <th className="text-left p-2">Inscrit ?</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {diagData.subscribers.map((s) => (
                                                    <tr key={s.user_id} className="border-t border-border/40">
                                                        <td className="p-2">{s.email}</td>
                                                        <td className="p-2"><code>{String(s.active_subscription ?? "null")}</code></td>
                                                        <td className="p-2"><code className={s.subscription_free_series ? "" : "text-destructive font-bold"}>{String(s.subscription_free_series ?? "null")}</code></td>
                                                        <td className="p-2"><code>{String(s.subscription_lookup_key ?? "—")}</code></td>
                                                        <td className="p-2">
                                                            {s.registered_for_event
                                                                ? <span className="text-primary">✅ {s.registration_status}{s.auto_registered ? " (auto)" : ""}</span>
                                                                : <span className="text-destructive">❌ non</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground italic">
                                💡 Un abonné avec <code>subscription_free_series: null</code> mais <code>active_subscription</code> renseigné explique un cron raté avant le fix du 1er février 2026. Le nouveau cron utilise désormais les deux champs en fallback.
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={!!regSearchData} onOpenChange={(o) => { if (!o) setRegSearchData(null); }}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="registrations-search-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Résultats de la recherche</DialogTitle>
                    </DialogHeader>
                    {regSearchData && (
                        <div className="space-y-4 text-sm">
                            <div className="text-xs text-muted-foreground">
                                Requête : <code className="bg-muted px-1.5 py-0.5 rounded">{regSearchData.query}</code> · {regSearchData.users_found} utilisateur(s) trouvé(s) · {regSearchData.registrations_found} inscription(s)
                            </div>
                            {regSearchData.registrations_found === 0 && regSearchData.users_found === 0 ? (
                                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-xs">
                                    <strong>Aucun utilisateur ne correspond.</strong> Le client n'a probablement <strong>jamais créé de compte</strong> avec cette adresse — donc n'a pas pu s'inscrire à un atelier (la connexion est obligatoire). Demande-lui de vérifier son adresse email et de s'inscrire d'abord sur le site.
                                </div>
                            ) : regSearchData.registrations_found === 0 ? (
                                <div className="rounded-lg border border-secondary/40 bg-secondary/10 p-4 text-xs">
                                    <strong>Utilisateur trouvé, mais aucune inscription à aucun atelier.</strong> Le client a peut-être seulement <strong>validé un code cadeau sans finaliser</strong> son inscription (la validation ne réserve pas la place — il faut cocher les CGV et cliquer sur "Confirmer l'inscription gratuite").
                                </div>
                            ) : (
                                <div className="rounded-2xl border border-border/60 overflow-hidden">
                                    <table className="w-full text-xs">
                                        <thead className="bg-muted/50">
                                            <tr>
                                                <th className="text-left p-2">Email</th>
                                                <th className="text-left p-2">Atelier</th>
                                                <th className="text-left p-2">Statut</th>
                                                <th className="text-left p-2">Prix</th>
                                                <th className="text-left p-2">Code</th>
                                                <th className="text-left p-2">Inscrit·e le</th>
                                                <th className="text-left p-2">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {regSearchData.registrations.map((r) => (
                                                <tr key={r.reg_id} className="border-t border-border/40" data-testid={`search-row-${r.reg_id}`}>
                                                    <td className="p-2 font-medium">{r.user_email}</td>
                                                    <td className="p-2">
                                                        <div>{r.event_title}</div>
                                                        {r.event_starts_at && <div className="text-muted-foreground">{new Date(r.event_starts_at).toLocaleDateString("fr-FR", {day:"numeric",month:"short",year:"numeric"})}</div>}
                                                    </td>
                                                    <td className="p-2">
                                                        {r.status === "confirmed"
                                                            ? <span className="text-primary">✅ confirmé</span>
                                                            : <span className="text-secondary">⏳ {r.status}</span>}
                                                    </td>
                                                    <td className="p-2">{(r.price ?? 0) === 0 ? "0 €" : `${r.price} €`}</td>
                                                    <td className="p-2 font-mono">{r.discount_code || "—"}</td>
                                                    <td className="p-2 text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : "—"}</td>
                                                    <td className="p-2">
                                                        <button
                                                            onClick={() => deleteRegistration(r.reg_id, `${r.user_email} · ${r.event_title}`)}
                                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-destructive/30 text-destructive hover:bg-destructive/10 text-[11px]"
                                                            data-testid={`delete-reg-${r.reg_id}`}
                                                            title="Supprimer cette inscription (utile pour nettoyer un pending_payment fantôme)"
                                                        >
                                                            <Trash2 className="w-3 h-3" /> Supprimer
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {regSearchData.users_with_no_registrations?.length > 0 && (
                                <div className="rounded-lg border border-secondary/40 bg-secondary/10 p-3 text-xs">
                                    <div className="font-medium mb-1">Comptes existants sans aucune inscription :</div>
                                    <ul className="list-disc pl-4 space-y-0.5">
                                        {regSearchData.users_with_no_registrations.map((u) => (
                                            <li key={u.user_id}><code>{u.email}</code> {u.name ? `· ${u.name}` : ""} {u.active_subscription ? `· abonné(e) ${u.active_subscription}` : ""}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={!!regsData} onOpenChange={(o) => { if (!o) setRegsData(null); }}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="registrants-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Inscrits à l'atelier</DialogTitle>
                    </DialogHeader>
                    {regsData && (
                        <div className="space-y-4 text-sm">
                            <div className="rounded-2xl border border-border/60 p-4 bg-muted/30">
                                <div className="font-semibold mb-2">{regsData.event?.title}</div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="text-muted-foreground">Débute :</span> {regsData.event?.starts_at ? new Date(regsData.event.starts_at).toLocaleString("fr-FR") : "—"}</div>
                                    <div><span className="text-muted-foreground">Capacité :</span> {regsData.event?.capacity}</div>
                                    <div><span className="text-muted-foreground">Confirmés :</span> <strong className="text-primary">{regsData.confirmed_count}</strong></div>
                                    <div><span className="text-muted-foreground">En attente :</span> <strong className={regsData.pending_count > 0 ? "text-secondary" : ""}>{regsData.pending_count}</strong></div>
                                </div>
                            </div>
                            {regsData.total === 0 ? (
                                <div className="text-xs text-muted-foreground p-4 rounded-lg bg-muted/40 border border-border/40 text-center">
                                    Aucune inscription pour cet atelier pour le moment.
                                </div>
                            ) : (
                                <div className="rounded-2xl border border-border/60 overflow-hidden">
                                    <table className="w-full text-xs">
                                        <thead className="bg-muted/50">
                                            <tr>
                                                <th className="text-left p-2">Email</th>
                                                <th className="text-left p-2">Nom</th>
                                                <th className="text-left p-2">Type</th>
                                                <th className="text-left p-2">Statut</th>
                                                <th className="text-left p-2">Prix</th>
                                                <th className="text-left p-2">Commande</th>
                                                <th className="text-left p-2">Inscrit·e le</th>
                                            </tr>
                                        </thead>
                                        <tbody data-testid="registrants-table-body">
                                            {regsData.registrations.map((r) => (
                                                <tr key={r.id} className="border-t border-border/40" data-testid={`registrant-row-${r.id}`}>
                                                    <td className="p-2 font-medium">{r.user_email || <span className="text-destructive">—</span>}</td>
                                                    <td className="p-2">{r.user_name || "—"}</td>
                                                    <td className="p-2">{r.kind_label}</td>
                                                    <td className="p-2">
                                                        {r.status === "confirmed"
                                                            ? <span className="text-primary">✅ confirmé</span>
                                                            : <span className="text-secondary">⏳ {r.status}</span>}
                                                    </td>
                                                    <td className="p-2">{(r.price ?? 0) === 0 ? <span className="text-primary">0 €</span> : `${r.price} €`}</td>
                                                    <td className="p-2 font-mono">{r.order_number || "—"}</td>
                                                    <td className="p-2 text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <p className="text-xs text-muted-foreground italic">
                                💡 Les inscriptions via <strong>code cadeau 100 %</strong> et via <strong>abonnement</strong> n'apparaissent pas dans l'onglet Commandes (car aucun paiement n'est créé) — elles ne sont visibles qu'ici.
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={!!replayEvent} onOpenChange={(o) => { if (!o) closeReplay(); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Envoyer le replay</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            Atelier : <strong>{replayEvent?.title}</strong><br/>
                            Envoyé automatiquement aux <strong>{replayEvent?.registered_count || 0} participant(s) confirmé(s)</strong>.
                        </p>
                        <div>
                            <Label>Lien SwissTransfer (ou toute URL de téléchargement)</Label>
                            <Input value={replayUrl} onChange={(e) => setReplayUrl(e.target.value)} placeholder="https://www.swisstransfer.com/d/..." className="rounded-full h-11" data-testid="replay-url-input" />
                        </div>
                        <div>
                            <Label>Message personnel (facultatif)</Label>
                            <Textarea value={replayMsg} onChange={(e) => setReplayMsg(e.target.value)} placeholder="Merci pour votre présence lumineuse…" className="rounded-2xl min-h-[120px]" data-testid="replay-msg-input" />
                        </div>
                        {replayEvent?.replay_sent_at && (
                            <div className="text-xs bg-secondary/10 border border-secondary/30 rounded-2xl px-4 py-3">
                                ⚠️ Un replay a déjà été envoyé le {new Date(replayEvent.replay_sent_at).toLocaleString("fr-FR")}. Renvoyer écrasera l'URL précédente.
                            </div>
                        )}
                        <Button onClick={sendReplay} disabled={replayBusy} className="w-full rounded-full h-12 bg-primary text-primary-foreground mt-2" data-testid="replay-send">
                            {replayBusy ? "Envoi en cours…" : "Envoyer le replay à tous les participants"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Maintenance — regenerate monthly calendar PDFs */}
            <div className="mt-16 rounded-3xl border border-primary/30 bg-primary/5 p-6 md:p-8" data-testid="maintenance-zone">
                <div className="flex items-start gap-3 mb-4">
                    <CalendarCheck className="w-6 h-6 text-primary shrink-0 mt-1" />
                    <div>
                        <h2 className="font-serif text-2xl mb-1">Maintenance</h2>
                        <p className="text-sm text-foreground/70 leading-relaxed max-w-2xl">
                            Régénère les PDFs des calendriers d'ateliers (mois en cours + mois suivant) à partir des événements enregistrés en base. Utile après avoir ajouté, modifié ou supprimé un événement, sans attendre la génération automatique (déclenchée 7 jours avant chaque nouveau mois).
                        </p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
                    onClick={regenerateCalendars}
                    data-testid="regenerate-calendars-btn"
                >
                    <CalendarCheck className="w-4 h-4 mr-2" /> Régénérer les calendriers PDF
                </Button>
                {/* Bouton "Synchroniser" affiché — nécessaire à chaque ajout d'un nouveau plan
                    dans PLANS (backend). Le bouton "Importer" reste masqué : il ne sert que
                    lors d'une migration preview → prod (déjà effectuée). */}
                <Button
                    variant="outline"
                    className="rounded-full border-primary/40 text-primary hover:bg-primary/10 ml-3"
                    onClick={syncPaypalPlans}
                    data-testid="sync-paypal-plans-btn"
                >
                    <Wallet className="w-4 h-4 mr-2" /> Synchroniser les plans PayPal récurrents
                </Button>
                {false && (
                <Button
                    variant="outline"
                    className="rounded-full border-primary/40 text-primary hover:bg-primary/10 ml-3"
                    onClick={importPaypalPlans}
                    data-testid="import-paypal-plans-btn"
                    title="À utiliser en production pour réutiliser les plans créés en preview (évite les doublons côté PayPal)"
                >
                    <Wallet className="w-4 h-4 mr-2" /> Importer les plans PayPal existants
                </Button>
                )}
            </div>

            {/* Danger zone — pre-launch data wipe. Masquée post-lancement pour éviter tout clic accidentel. */}
            {false && (
            <div className="mt-16 rounded-3xl border-2 border-destructive/40 bg-destructive/5 p-6 md:p-8" data-testid="danger-zone">
                <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className="w-6 h-6 text-destructive shrink-0 mt-1" />
                    <div>
                        <h2 className="font-serif text-2xl text-destructive mb-1">Zone dangereuse</h2>
                        <p className="text-sm text-foreground/70 leading-relaxed max-w-2xl">
                            Efface toutes les activités clients (commandes, réservations, inscriptions ateliers, abonnements en cours, programmes achetés, listes d'attente) et remet le compteur de facture à zéro. À utiliser <strong>une seule fois</strong>, juste avant la mise en ligne, pour repartir d'une page blanche après les tests. Les <strong>comptes utilisateurs, articles, événements, témoignages, codes cadeaux</strong> sont préservés.
                        </p>
                    </div>
                </div>
                <Button
                    variant="destructive"
                    className="rounded-full"
                    onClick={() => { setResetInput(""); setResetOpen(true); }}
                    data-testid="open-reset-dialog"
                >
                    <AlertTriangle className="w-4 h-4 mr-2" /> Réinitialiser les données clients
                </Button>
            </div>
            )}

            {false && (
            <Dialog open={resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(false); setResetInput(""); } }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl text-destructive flex items-center gap-2">
                            <AlertTriangle className="w-6 h-6" /> Confirmation requise
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-foreground/85 leading-relaxed">
                            Cette action va <strong>définitivement supprimer</strong> :
                        </p>
                        <ul className="text-sm text-foreground/75 list-disc pl-5 space-y-1">
                            <li>Toutes les commandes (Stripe, PayPal, virements)</li>
                            <li>Toutes les réservations de séances</li>
                            <li>Toutes les inscriptions aux ateliers</li>
                            <li>Tous les programmes d'accompagnement achetés</li>
                            <li>Tous les cadeaux offerts</li>
                            <li>Toutes les listes d'attente</li>
                            <li>Les abonnements actifs (les comptes restent)</li>
                        </ul>
                        <p className="text-sm text-foreground/85">
                            Le compteur de commandes redémarrera à <strong className="font-mono">F000001</strong> pour ton premier vrai client.
                        </p>
                        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4">
                            <Label className="text-destructive font-medium">Pour confirmer, tape <span className="font-mono bg-destructive/20 px-2 py-0.5 rounded">RESET</span> ci-dessous :</Label>
                            <Input
                                value={resetInput}
                                onChange={(e) => setResetInput(e.target.value)}
                                placeholder="RESET"
                                className="rounded-full h-11 mt-2 font-mono uppercase tracking-widest"
                                data-testid="reset-confirm-input"
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1 rounded-full"
                                onClick={() => { setResetOpen(false); setResetInput(""); }}
                                data-testid="reset-cancel"
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="destructive"
                                className="flex-1 rounded-full disabled:opacity-40"
                                disabled={resetInput !== "RESET" || resetBusy}
                                onClick={runResetCustomerData}
                                data-testid="reset-confirm"
                            >
                                {resetBusy ? "Réinitialisation…" : "Confirmer la réinitialisation"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            )}
        </div>
    );
}